/*
 * Fig & Bloom - delivery suburb / date picker popup.
 *
 * This was a single 154,942-byte inline <script> inside the popup markup, parsed on every
 * collection, product and home page whether or not the visitor ever opened the picker. It is
 * used on collection pages (triggered by the date input and by add-to-cart), so it cannot be
 * gated off them. It is now loaded on the visitor's first interaction with the page, and
 * warmed during idle time before that, so in practice it is already cached by the time
 * anyone clicks.
 *
 * Loaded by the stub in snippets/delivery-suburb-popup.liquid, which also:
 *  - supplies the four Liquid-derived values via window.fbPopupConfig, because .js assets are
 *    not Liquid-rendered
 *  - shims this file's public entry points (openPopup, openPopupAtStep, openPreSalePopup,
 *    popupSetupDatePicker, resetToLocationStep and the rest) so a caller that fires before
 *    the asset has arrived is queued and replayed rather than dropped. Those entry points are
 *    called from product-form.js, product-card-delivery.js, the buy-button snippets and
 *    several sections; dropping one of those calls would break add-to-cart.
 *
 * Every function here is declared at global scope on purpose: that is how the callers above
 * reach it, and it is how the real declarations overwrite the stub's shims on load.
 */

// Defensive default. The stub in snippets/delivery-suburb-popup.liquid always sets this
// before the asset can load, but a cached asset pulled in on a page that did not render the
// stub would otherwise throw on the first property read and take the whole popup down.
window.fbPopupConfig = window.fbPopupConfig || {
  isProductPage: false,
  autoOpenEnabled: false
};

// Runs cb now if the document is already parsed, otherwise on DOMContentLoaded. Needed
// because this file used to run inline during parse and several blocks below assumed that.
function fbPopupOnReady(cb) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cb);
  } else {
    cb();
  }
}
// Global variables for popup date picker
var popupCurrentLocation = null;
var popupDatePickerSettings = null;
var popupFlatpickr = null;
var popupSelectedLocality = null;
var popupSelectedState = null;
var popupSelectedPcode = null;
var popupSelectedDateStr = null;
var popupSelectedISODate = null;

// ========================================
// CLICK & COLLECT (fulfilment method)
// ========================================
// The popup serves two fulfilment paths through the same machinery: "delivery"
// (type a suburb/postcode) and "collect" (pick a studio, which fixes the
// suburb/state/postcode to the studio's). The collection date is stored as the
// delivery date, so everything downstream — cart line properties, capacity
// checks, city-scoped collection grids — is unchanged. Must mirror the studio
// buttons in snippets/delivery-suburb-popup.liquid.
var FB_COLLECT_STUDIOS = {
    'Sydney':    { locality: 'Camperdown',  state: 'NSW', pcode: '2050', address: '62-64 Australia St, Camperdown NSW' },
    'Melbourne': { locality: 'Fairfield',   state: 'VIC', pcode: '3078', address: '274 Wingrove St, Fairfield VIC' },
    'Brisbane':  { locality: 'Bowen Hills', state: 'QLD', pcode: '4006', address: '31 Jeays St, Bowen Hills QLD' }
};
var popupFulfilmentMethod = 'delivery';   // 'delivery' | 'collect'
var popupSelectedStudio = null;           // key into FB_COLLECT_STUDIOS while collecting


// Product page detection (set by Liquid) - used for delivery settings override
var isProductPage = window.fbPopupConfig.isProductPage;

// Theme setting (Customizer > Delivery Popup): auto-open this popup on collection page load.
// When false, the popup still opens on add-to-cart and via the header delivery pills.
var popupAutoOpenEnabled = window.fbPopupConfig.autoOpenEnabled;


// Shared delivery settings and cookies for every catalogue entry point.
function getMinDeliveryDays(datePickerSettings) {
    return parseInt(datePickerSettings.minDeliveryDays);
}
function getDeliveryDateCookieName() { return 'DeliveryDate'; }
function getISODateCookieName() { return 'ISODate'; }
function getStoredDeliveryDate() { return getCookie('DeliveryDate'); }
function getStoredISODate() { return getCookie('ISODate'); }
function hasStoredDeliveryDate() { return !!getStoredDeliveryDate(); }

// ========================================
// CLICK & COLLECT HELPERS
// ========================================

// Hydrate fulfilment state from cookies (run at init; safe to re-run)
function popupHydrateFulfilmentFromCookies() {
    var method = getCookie('fulfilmentMethod');
    var studio = getCookie('collectStudio');
    if (method === 'collect' && studio && FB_COLLECT_STUDIOS[studio]) {
        popupFulfilmentMethod = 'collect';
        popupSelectedStudio = studio;
    } else {
        popupFulfilmentMethod = 'delivery';
        popupSelectedStudio = null;
    }
}

// Pre-sale keeps its own date selection flow.
function popupCollectAvailable() {
    return !window.pendingPreSaleForm;
}

// True when the current selection really is a studio collection: mode is
// collect AND the active postcode is the chosen studio's. The postcode match
// guards against a stale fulfilmentMethod cookie after the delivery location
// was changed through a path that doesn't know about collect (e.g. the PDP
// delivery app writing the location cookies directly).
function popupIsCollectSelection(pcode) {
    if (popupFulfilmentMethod !== 'collect' || !popupSelectedStudio) return false;
    var studio = FB_COLLECT_STUDIOS[popupSelectedStudio];
    return !!studio && String(studio.pcode) === String(pcode);
}

// Render the fulfilment toggle + panels to match the given method.
// Display-only: the cookies are written when a selection completes
// (popupFinishSelection), not when the user flips tabs.
function popupSetFulfilmentMethod(method) {
    if (method === 'collect' && !popupCollectAvailable()) {
        method = 'delivery';
    }
    popupFulfilmentMethod = method;

    var toggle = document.getElementById('popupFulfilmentToggle');
    var deliveryTab = document.getElementById('fulfilmentDeliveryTab');
    var collectTab = document.getElementById('fulfilmentCollectTab');
    var deliveryPanel = document.getElementById('deliveryModePanel');
    var collectPanel = document.getElementById('collectModePanel');
    if (!toggle || !deliveryTab || !collectTab || !deliveryPanel || !collectPanel) return;

    // Pre-sale is delivery-only: no toggle.
    toggle.style.display = popupCollectAvailable() ? 'flex' : 'none';

    var isCollect = method === 'collect';
    deliveryTab.classList.toggle('active', !isCollect);
    deliveryTab.setAttribute('aria-pressed', String(!isCollect));
    collectTab.classList.toggle('active', isCollect);
    collectTab.setAttribute('aria-pressed', String(isCollect));
    deliveryPanel.style.display = isCollect ? 'none' : 'block';
    collectPanel.style.display = isCollect ? 'block' : 'none';

    // Keep the suburb autocomplete dropdown from floating over the collect panel
    if (isCollect) {
        var results = document.getElementById('popupAutocompleteResults');
        if (results) results.style.display = 'none';
    }

    // Highlight the remembered studio
    document.querySelectorAll('.popup-studio-option').forEach(function(btn) {
        btn.classList.toggle('selected', isCollect && btn.getAttribute('data-studio') === popupSelectedStudio);
    });
}

// Hydrate at parse time, not only in initializePopup: a queued openPopup() call
// replays on asset load, BEFORE initializePopup's 50ms timer, and renders the
// toggle from this state. (initializePopup re-hydrates; that's a no-op then.)
popupHydrateFulfilmentFromCookies();



// STEP 1: Add this at the very top of your popup modal script
window.RedirectManager = (function() {
    let hasRedirected = false;
    let isRedirecting = false;
    
    // P5: the location -> grid-filter query map, built from the app's config rather
    // than hand-maintained. Adelaide had to be added here by hand; a 6th city would
    // have needed the same edit in three separate places. window.fbLocations now
    // carries each location's numeric Shopify id, so the map builds itself.
    // The five hardcoded entries remain as the fallback for a store whose config
    // hasn't been regenerated — dynamic entries are layered on top, so a location
    // present in both resolves to the same query string either way.
    const locationFilters = (function() {
        const filters = {
            'Brisbane': '?filter.v.m.location.70541672681=1&filter.v.availability=1',
            'Melbourne': '?filter.v.m.location.70507725033=1&filter.v.availability=1',
            'Sydney': '?filter.v.m.location.70541639913=1&filter.v.availability=1',
            'RestofAustralia': '?filter.v.m.location.74290069737=1&filter.v.availability=1',
            'Adelaide': '?filter.v.m.location.90760315113=1&filter.v.availability=1'
        };
        (window.fbLocations || []).forEach(function(l) {
            if (l && l.keyPrefix && l.locationId) {
                filters[l.keyPrefix] = '?filter.v.m.location.' + l.locationId + '=1&filter.v.availability=1';
            }
        });
        return filters;
    })();
    
    function getTargetUrl(expectedParams) {
        const currentUrl = new URL(window.location.href);
        const required = new URLSearchParams(expectedParams || '');
        const locationKeys = Array.from(currentUrl.searchParams.keys()).filter(function(key) {
            return key.startsWith('filter.v.m.location.');
        });
        // Unknown locations remove only obsolete city constraints. Keep stock and
        // shopper filters intact; no city filter is invented for an unknown bucket.
        if (!expectedParams) {
            if (!locationKeys.length) return null;
            locationKeys.forEach(function(key) { currentUrl.searchParams.delete(key); });
            currentUrl.searchParams.delete('page');
            return currentUrl.href;
        }
        const alreadyScoped = locationKeys.length === 1 &&
            required.has(locationKeys[0]) &&
            Array.from(required).every(function(entry) {
                const values = currentUrl.searchParams.getAll(entry[0]);
                return values.length === 1 && values[0] === entry[1];
            });
        if (alreadyScoped) {
            return null;
        }

        // Retain the collection view, shopper filters/sort and preview context.
        // Only replace delivery-owned filters; an old city's page is not valid
        // for the newly scoped result set. URL also keeps the fragment intact.
        locationKeys.forEach(function(key) { currentUrl.searchParams.delete(key); });
        currentUrl.searchParams.delete('filter.v.availability');
        currentUrl.searchParams.delete('page');
        required.forEach(function(value, key) { currentUrl.searchParams.set(key, value); });
        return currentUrl.href;
    }

    function checkAndRedirect() {
        if (hasRedirected || isRedirecting) {
            return false;
        }

        if (!window.location.pathname.startsWith('/collections')) {
            return false;
        }
        
        const locality = getCookie('deliverySuburb');
        const pcode = getCookie('deliveryPostcode');
        
        if (!locality || !pcode) {
            return false;
        }
        
        const location = findPostcodeLocation(pcode);
        const expectedParams = locationFilters[location];
        
        if (!expectedParams) {
            return false;
        }
        
        const newUrl = getTargetUrl(expectedParams);
        if (!newUrl) {
            hasRedirected = true;
            return false;
        }

        isRedirecting = true;
        window.location.replace(newUrl);
        return true;
    }
    
    function resetRedirectState() {
        hasRedirected = false;
        isRedirecting = false;
    }
    
    return {
        checkAndRedirect: checkAndRedirect,
        resetRedirectState: resetRedirectState,
        isRedirecting: function() { return isRedirecting; },
        hasRedirected: function() { return hasRedirected; },
        getTargetUrl: getTargetUrl,
        getFilterParams: function(location) { return locationFilters[location] || null; }
    };
})();

// STEP 2b: In-place location filtering (Shopify Section Rendering API).
// The primary way the grid gets scoped to the visitor's stored delivery location —
// on initial load (STEP 2, below the swap machinery) and when the popup/date flows
// change the suburb: swaps #ProductGridContainer with the server-rendered
// filtered grid and silently updates the URL (replaceState — auto-applied state,
// so Back doesn't step through the unfiltered view). The header delivery pills
// remain the visitor-facing indicator of the applied location.
// Any failure falls back to a hard navigation to the SAME filtered URL, so the
// worst case equals today's redirect behavior.

// Generation counter: a newer swap supersedes any in-flight one, so a slow
// first fetch (or its timeout) can never repaint/navigate to a stale city after
// the visitor has already changed suburb again.
// Only a call that actually starts a swap takes a number. A call that returns
// early ("already filtered for this city", which is what a second call sees once
// the first has replaceState'd) used to bump it too, voiding the in-flight swap
// that had already faded the grid, so the grid stayed at 0.4 opacity for good.
var fbLocationFilterSeq = 0;

window.applyLocationFilterInPlace = function() {
    try {
        if (!window.location.pathname.startsWith('/collections')) return;

        var locality = getCookie('deliverySuburb');
        var pcode = getCookie('deliveryPostcode');
        if (!locality || !pcode) return;

        var location = findPostcodeLocation(pcode);
        var filterParams = window.RedirectManager.getFilterParams(location);

        // Initial load, popstate and popup completion all use the same semantic
        // delivery scope as the redirect safety net, retaining discovery context.
        var targetUrl = window.RedirectManager.getTargetUrl(filterParams);
        if (!targetUrl) return;
        performLocationGridSwap(targetUrl, ++fbLocationFilterSeq);
    } catch (err) {
        console.warn('[LocationFilter] In-place filter error, falling back to redirect:', err);
        if (window.RedirectManager) {
            window.RedirectManager.resetRedirectState();
            window.RedirectManager.checkAndRedirect();
        }
    }
};

// Shared swap machinery: replaceState to targetUrl, fetch the grid section for that
// URL, swap #ProductGridContainer in place, re-init card JS, then run onSwapSuccess.
// Any failure falls back to a hard navigation to targetUrl (worst case = the old
// redirect behavior). A newer fbLocationFilterSeq voids this invocation entirely.
function performLocationGridSwap(targetUrl, seq, onSwapSuccess) {
    var isStale = function() { return seq !== fbLocationFilterSeq; };

    // NOTE: fallback must navigate to the URL directly. After replaceState below,
    // RedirectManager.checkAndRedirect() no-ops (URL already has the params), so it
    // can NOT serve as the failure fallback. Small delay so an open cart drawer or
    // unavailable-item toast gets a moment on screen before the navigation.
    var fallbackNavigate = function() {
        if (isStale()) return;
        setTimeout(function() {
            if (!isStale()) window.location.replace(targetUrl);
        }, 1200);
    };

    var grid = document.getElementById('ProductGridContainer');
    var gridInner = document.getElementById('product-grid');
    if (!grid || !gridInner || !gridInner.dataset.id) {
        fallbackNavigate();
        return;
    }

    // If the current grid rendered zero product cards, their per-card inline
    // scripts never ran, so the 'ajaxProductGridUpdated' slider re-init hook has
    // no listeners — swapped-in cards would get dead image sliders. A full
    // navigation re-runs everything correctly in that (rare) case.
    if (!grid.querySelector('[data-product-id]')) {
        fallbackNavigate();
        return;
    }

    // Update the URL first; from here every RedirectManager check no-ops.
    // (If sorting/filtering facet UI is ever re-enabled on collections —
    // enable_sorting is currently false everywhere and the facets aside is
    // commented out — that form's params must be synced here too.)
    history.replaceState(history.state, '', targetUrl);

    grid.style.transition = 'opacity 0.25s ease';
    grid.style.opacity = '0.4';
    grid.setAttribute('aria-busy', 'true');

    var sectionUrl = new URL(targetUrl, window.location.href);
    sectionUrl.hash = '';
    sectionUrl.searchParams.set('section_id', gridInner.dataset.id);
    var abandoned = false;
    var timer = setTimeout(function() {
        abandoned = true; // a late fetch response must not race the fallback navigation
        fallbackNavigate();
    }, 6000);

    fetch(sectionUrl.href)
        .then(function(res) {
            if (!res.ok) throw new Error('Section render HTTP ' + res.status);
            return res.text();
        })
        .then(function(html) {
            clearTimeout(timer);
            if (isStale() || abandoned) return;
            var freshGrid = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
            if (!freshGrid) throw new Error('No ProductGridContainer in section response');
            grid.innerHTML = freshGrid.innerHTML;
            // Un-fade as soon as the new cards are in, so an error in the
            // re-init hooks below can't leave a loaded grid washed out.
            grid.style.opacity = '1';
            grid.removeAttribute('aria-busy');

            // Re-initialize card sliders (existing theme hook — same event the
            // best-sellers/bundles AJAX paths dispatch) and re-apply the
            // client-side delivery-date filtering to the swapped-in cards.
            document.dispatchEvent(new CustomEvent('ajaxProductGridUpdated'));
            if (typeof filterProductsByDeliveryDate === 'function') {
                filterProductsByDeliveryDate();
            }

            if (typeof onSwapSuccess === 'function') {
                onSwapSuccess();
            }
        })
        .catch(function(err) {
            clearTimeout(timer);
            if (isStale() || abandoned) return;
            console.warn('[LocationFilter] In-place filter failed, falling back to navigation:', err);
            fallbackNavigate();
        });
}

// STEP 2: Initial-load location scoping.
// This file loads lazily (idle warm / first interaction — see the loader stub in
// snippets/delivery-suburb-popup.liquid), so by the time it executes the collection
// page has already painted. The old RedirectManager.checkAndRedirect() call here
// fired location.replace() seconds into the visit for any returning visitor with
// delivery cookies — the page loaded, then visibly loaded again. Scope the grid in
// place instead; hard navigation survives only as the swap's own failure fallback.
(function() {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            window.applyLocationFilterInPlace();
        });
    } else {
        window.applyLocationFilterInPlace();
    }
})();

// STEP 8: Browser navigation handler — re-scope in place for the same reason.
// The reset keeps RedirectManager's one-shot flags from suppressing its remaining
// safety-net callers (checkAndShowPopup, the header form) on a history walk.
window.addEventListener('popstate', function() {
    if (window.RedirectManager) {
        window.RedirectManager.resetRedirectState();
    }
    window.applyLocationFilterInPlace();
});

document.addEventListener('click', function(event) {
    const popupModal = document.getElementById('popupModal');
    // Check if the popup is currently displayed
    if (popupModal.style.display === 'flex') {
        // Prevent any action that might enable scrolling
        event.preventDefault();
        return false;
    }
    // Any other global click behavior can go here
});

// Enhanced keyboard-aware positioning for mobile devices
(function() {
  const popupModal = document.getElementById('popupModal');
  const popupBackground = document.getElementById('popupBackground');
  const popupContent = popupModal.querySelector('.popup-modal-content');
  const input = document.getElementById('popupDeliverySuburb');
  const resultsContainer = document.getElementById('popupAutocompleteResults');
  
  let originalStyles = {};
  let keyboardOpen = false;
  let originalViewportHeight = window.innerHeight;
  
  // iOS-specific: Debounce timer for visualViewport resize
  let iOSResizeDebounceTimer = null;

  function isMobileDevice() {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
  }

  function isIOS() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  }
  
  // iOS-specific: Update original viewport height when popup opens
  function updateOriginalViewportHeight() {
    originalViewportHeight = window.innerHeight;
  }

  function detectKeyboard() {
    const currentHeight = window.innerHeight;
    const heightDifference = originalViewportHeight - currentHeight;
    
    // More sensitive detection for different devices
const threshold = isIOS() ? 200 : 100;

    return heightDifference > threshold;
  }

  function saveOriginalStyles() {
    originalStyles = {
      position: popupContent.style.position,
      top: popupContent.style.top,
      left: popupContent.style.left,
      transform: popupContent.style.transform,
      width: popupContent.style.width,
      maxWidth: popupContent.style.maxWidth,
      maxHeight: popupContent.style.maxHeight,
      overflowY: popupContent.style.overflowY,
      padding: popupContent.style.padding,
      marginTop: popupContent.style.marginTop,
      marginBottom: popupContent.style.marginBottom
    };
  }

  function adjustForKeyboard() {
    // iOS-specific: Don't adjust if popup is not visible
    // Check for 'none' instead of !== 'block' because adjustForKeyboard itself
    // switches display to 'flex', and the old check caused all subsequent calls
    // (including keyboard-close restoration) to bail out permanently.
    if (isIOS() && popupModal.style.display === 'none') {
      return;
    }
    if (!isMobileDevice()) return;

    const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    const isKeyboardVisible = detectKeyboard();
    
    if (isKeyboardVisible && !keyboardOpen) {
      keyboardOpen = true;

      // Lock page scrolling when keyboard is active — but ONLY while the
      // popup is actually on screen. On iOS, dismissing the popup animates
      // the visual viewport, which can re-fire this handler after the
      // popup's close cleanup has already run; locking then would leave the
      // page permanently unscrollable with nothing left to unlock it.
      const fbPopupVisible = popupModal
        && popupModal.style.display !== 'none'
        && popupModal.offsetParent !== null;
      if (fbPopupVisible) {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      }
      
      // Calculate available space
      const availableHeight = viewportHeight;
      const topOffset = 20; // Top spacing
      const paddingBuffer = 20;
      const maxModalHeight = availableHeight - topOffset - paddingBuffer;
      
      // iOS-specific: Set modal and background heights to visual viewport height
      // This prevents the white overlay caused by 100vh not updating on iOS keyboard
      if (isIOS()) {
        popupModal.style.height = `${viewportHeight}px`;
        popupModal.style.display = 'flex';
        popupModal.style.justifyContent = 'center'; // Center horizontally on iOS
        popupModal.style.alignItems = 'flex-start'; // Align to top
        popupModal.style.paddingTop = '50px'; // Add top padding for iOS status bar/notch
        if (popupBackground) {
          popupBackground.style.height = `${viewportHeight}px`;
        }
        // iOS homepage: Temporarily remove fixed positioning from sticky section to prevent interference
        const homepageDeliverySection = document.getElementById('deliveryHomepageSection');
        if (homepageDeliverySection && homepageDeliverySection.classList.contains('is-sticky')) {
          homepageDeliverySection.style.position = 'absolute';
          homepageDeliverySection.style.visibility = 'hidden';
        }
      }
      
      // Adjust modal positioning and size - move to top when keyboard opens
      if (!isIOS()) {
        popupModal.style.alignItems = 'flex-start';
        popupModal.style.paddingTop = `${topOffset}px`;
      }
      popupModal.style.overflow = 'hidden'; // Prevent modal from scrolling
      
      popupContent.style.position = 'relative';
      popupContent.style.top = 'auto';
      popupContent.style.width = '95%';
      popupContent.style.maxWidth = '95%';
      popupContent.style.overflowY = 'auto';
      popupContent.style.padding = '20px 15px';
      popupContent.style.marginTop = '0';
      popupContent.style.marginBottom = '20px';
      
      // iOS-specific: More constrained height to fit above keyboard properly
      if (isIOS()) {
        const iOSMaxHeight = viewportHeight - 100; // Account for status bar + spacing
        popupContent.style.maxHeight = `${iOSMaxHeight}px`;
        popupContent.style.left = 'auto';
        popupContent.style.transform = 'none';
        popupContent.style.marginLeft = 'auto';
        popupContent.style.marginRight = 'auto';
      } else {
        // Android: Use calculated max height
        popupContent.style.maxHeight = `${maxModalHeight}px`;
        popupContent.style.left = 'auto';
        popupContent.style.transform = 'none';
      }
      
      // Adjust dropdown positioning for keyboard with better scroll handling
      resultsContainer.style.position = 'absolute';
      resultsContainer.style.maxHeight = '140px';
      resultsContainer.style.overflowY = 'auto';
      resultsContainer.style.zIndex = '10002';
      resultsContainer.style.touchAction = 'pan-y'; // Allow vertical scrolling in dropdown
      
      // Add keyboard-specific class
      popupContent.classList.add('keyboard-active');
      
    } else if (!isKeyboardVisible && keyboardOpen) {
      keyboardOpen = false;

      // Keep overflow:hidden — popup is still open, scroll-lock managed by openPopup/closePopup

      // iOS-specific: Restore modal and background heights and reset centering
      if (isIOS()) {
        popupModal.style.height = '100%';
        popupModal.style.justifyContent = 'center';
        popupContent.style.marginLeft = '';
        popupContent.style.marginRight = '';
        if (popupBackground) {
          popupBackground.style.height = '100%';
        }
        // iOS homepage: Restore sticky section positioning
        const homepageDeliverySection = document.getElementById('deliveryHomepageSection');
        if (homepageDeliverySection && homepageDeliverySection.classList.contains('is-sticky')) {
          homepageDeliverySection.style.position = '';
          homepageDeliverySection.style.visibility = '';
        }
      }
      
      // Restore center alignment when keyboard closes
      popupModal.style.alignItems = 'center';
      popupModal.style.paddingTop = '0';
      popupModal.style.overflow = 'auto';

      restoreOriginalStyles();
      popupContent.classList.remove('keyboard-active');

      // If the popup is no longer on screen (keyboard dismissal raced the
      // popup close), make sure the page scroll locks are released.
      const fbPopupGone = !popupModal
        || popupModal.style.display === 'none'
        || popupModal.offsetParent === null;
      if (fbPopupGone) {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    }
  }

  // Safety net: this file is the only code in the theme that sets an inline
  // overflow lock on <html>. If that lock is ever present while no delivery
  // popup is visible, the page is stuck unscrollable — clear it.
  setInterval(function () {
    if (document.documentElement.style.overflow !== 'hidden') return;
    const anyPopupVisible = Array.prototype.some.call(
      document.querySelectorAll('.popup-modal'),
      function (m) { return m.style.display !== 'none' && m.offsetParent !== null; }
    );
    if (!anyPopupVisible) {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  }, 1500);

  function restoreOriginalStyles() {
    Object.keys(originalStyles).forEach(key => {
      popupContent.style[key] = originalStyles[key];
    });
    
    // Reset dropdown styles
    resultsContainer.style.position = '';
    resultsContainer.style.maxHeight = '';
    resultsContainer.style.zIndex = '';
    resultsContainer.style.touchAction = '';
    
    // Reset modal positioning styles
    popupModal.style.alignItems = '';
    popupModal.style.paddingTop = '';
    popupModal.style.overflow = '';
  }

  if (isMobileDevice()) {
    // Save original styles when modal opens
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'style' && popupModal.style.display === 'block') {
          saveOriginalStyles();
          // iOS-specific: Update viewport height when popup opens
          if (isIOS()) {
            updateOriginalViewportHeight();
          }
        }
      });
    });
    
    observer.observe(popupModal, { attributes: true, attributeFilter: ['style'] });

    // iOS-specific touch tracking to prevent false blur events during input interaction
    let isTouchingPopupInput = false;
    
    if (isIOS()) {
      // Track touch state on input field
      input.addEventListener('touchstart', () => { 
        isTouchingPopupInput = true; 
      }, { passive: true });
      
      input.addEventListener('touchend', () => { 
        setTimeout(() => { isTouchingPopupInput = false; }, 150); 
      }, { passive: true });
      
      // Track touch state on autocomplete results
      resultsContainer.addEventListener('touchstart', () => { 
        isTouchingPopupInput = true; 
      }, { passive: true });
      
      resultsContainer.addEventListener('touchend', () => { 
        setTimeout(() => { isTouchingPopupInput = false; }, 150); 
      }, { passive: true });
    }

    // Listen for various events that indicate keyboard state changes
    input.addEventListener('focus', () => setTimeout(adjustForKeyboard, isIOS() ? 600 : 300));
    
    // Modified blur handler with iOS touch guard
    input.addEventListener('blur', () => {
      if (isIOS() && isTouchingPopupInput) {
        // On iOS, don't adjust keyboard if user is actively touching input/results
        return;
      }
      setTimeout(adjustForKeyboard, isIOS() ? 800 : 400);
    });

    // Visual viewport API support (modern browsers)
    if (window.visualViewport) {
      if (isIOS()) {
        // iOS-specific: Debounce visualViewport resize to prevent rapid-fire adjustments
        window.visualViewport.addEventListener('resize', () => {
          clearTimeout(iOSResizeDebounceTimer);
          iOSResizeDebounceTimer = setTimeout(adjustForKeyboard, 250);
        });
      } else {
        // Android and other devices: Direct handler
        window.visualViewport.addEventListener('resize', adjustForKeyboard);
      }
    }

    // Fallback for older browsers
    window.addEventListener('resize', () => {
      setTimeout(adjustForKeyboard, 100);
    });

    // Additional iOS specific handling
    if (isIOS()) {
      document.addEventListener('focusin', adjustForKeyboard);
      
      // Improved focusout handler - check if focus stays within popup
      document.addEventListener('focusout', (e) => {
        // If focus moved to another element within the popup, don't adjust
        if (e.relatedTarget && popupModal.contains(e.relatedTarget)) {
          return;
        }
        // If user is actively touching input/results, don't adjust
        if (isTouchingPopupInput) {
          return;
        }
        setTimeout(adjustForKeyboard, 500);
      });
    }
  }
})();

// Utility functions (ensure these are available)
function setCookie(name, value, days) {
    var redate = window.fbCartDateGuard && window.fbCartDateGuard.location && window.fbCartDateGuard.location();
    if (redate && Object.prototype.hasOwnProperty.call(redate, name)) return;
    var expires = "";
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
    // One copy per name: drop any copy in another scope (.figandbloom.com, a
    // sub-path) that would be read before this one (snippets/fb-delivery-date-guard.liquid).
    if (window.fbDeliveryDate && window.fbDeliveryDate.settle) window.fbDeliveryDate.settle(name);
}

function getCookie(name) {
    var redate = window.fbCartDateGuard && window.fbCartDateGuard.location && window.fbCartDateGuard.location();
    if (redate && Object.prototype.hasOwnProperty.call(redate, name)) return redate[name];
    var nameEQ = name + "=";
    var ca = document.cookie.split(';');
    for(var i=0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1,c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
    }
    return null;
}

function clearCookie(name) {
    setCookie(name, '', -1);
}

// ========================================
// POSTCODE PATTERN MATCHING (for override settings)
// ========================================
function popupMatchesPattern(pattern, postcode) {
    if (pattern.endsWith('*')) {
        return postcode.startsWith(pattern.slice(0, -1));
    } else if (pattern.includes(',')) {
        return pattern.split(',').includes(postcode);
    }
    return pattern === postcode;
}


// ========================================
// CAPACITY CHECK FUNCTIONS FOR POPUP
// ========================================

// Function to check if delivery capacity is reached for a specific location and date
function popupCheckDeliveryCapacity(location, dateString) {
    // Return false if no capacity data is available
    if (!window.deliveryCapacitySettings || !window.deliveryCapacitySettings.capacityByLocation) {
        return false;
    }
    
    // Get capacity data for the location
    const locationData = window.deliveryCapacitySettings.capacityByLocation[location];
    if (!locationData || !locationData[dateString]) {
        return false;
    }
    
    const capacity = locationData[dateString];
    
    // 0 means unlimited capacity
    if (capacity === 0) {
        console.log(`Unlimited capacity for ${location} on ${dateString}`);
        return false;
    }
    
    // For today, we can check the enhanced todayCapacity data
    const today = window.deliveryCapacitySettings?.currentDate;
    if (dateString === today && window.todayCapacity && window.todayCapacity.capacityStatus) {
        const locationStatus = window.todayCapacity.capacityStatus[location];
        if (locationStatus) {
            const isReached = locationStatus.isReached;
            console.log(`Capacity check for ${location} on ${dateString}: ${locationStatus.orders}/${locationStatus.capacity} - ${isReached ? 'REACHED' : 'AVAILABLE'}`);
            return isReached;
        }
    }
    
    // For other dates, check if we have order count data in the capacityByDate structure
    if (window.deliveryCapacitySettings.capacityByDate && window.deliveryCapacitySettings.capacityByDate[dateString]) {
        const dateCapacityData = window.deliveryCapacitySettings.capacityByDate[dateString];
        
        // Check if this date has order count data
        if (dateCapacityData.orderCounts) {
            const orderCount = dateCapacityData.orderCounts[location.toLowerCase()] || 0;
            const isReached = capacity > 0 && orderCount >= capacity;
            
            console.log(`Capacity check for ${location} on ${dateString}: ${orderCount}/${capacity} - ${isReached ? 'REACHED' : 'AVAILABLE'}`);
            return isReached;
        }
    }
    
    // If we have no order count data for this date, assume available (conservative approach)
    console.log(`No order count data for ${location} on ${dateString}, assuming available`);
    return false;
}

// Function to check if capacity is reached specifically for today
function popupIsCapacityReachedForToday(location) {
    if (!window.deliveryCapacitySettings || !window.deliveryCapacitySettings.currentDate) {
        console.log('No current date in capacity settings');
        return false;
    }
    
    const today = window.deliveryCapacitySettings.currentDate;
    return popupCheckDeliveryCapacity(location, today);
}

// Function to check if capacity is reached for any specific date
function popupIsCapacityReachedForDate(location, dateString) {
    return popupCheckDeliveryCapacity(location, dateString);
}


// Function to check capacity for any date (used for stored date validation)
function popupCheckCapacityForDate(location, dateString) {
    // This uses the same logic as popupCheckDeliveryCapacity
    return popupCheckDeliveryCapacity(location, dateString);
}


// Function to check if there are app-level blocked dates for today
function popupHasAppLevelBlockedDatesForToday(datePickerSettings, timezone) {
    if (!datePickerSettings || !datePickerSettings.selectedBlockedDates || datePickerSettings.selectedBlockedDates.length === 0) {
        return false;
    }
    
    const nowInTargetTimeZone = moment.tz(timezone);
    const todayFormatted = nowInTargetTimeZone.format('YYYY-MM-DD');
    
    // Check only app-level blocked dates (not product-specific ones)
    const appBlockedDates = datePickerSettings.selectedBlockedDates || [];
    
    const isBlocked = appBlockedDates.some(blockedDate => {
        const blockedMoment = popupCreateAustraliaDate(blockedDate, timezone);
        const blockedFormatted = blockedMoment.format('YYYY-MM-DD');
        return blockedFormatted === todayFormatted;
    });
    
    return isBlocked;
}

// Function to check if pre-sale is enabled for the current location
function popupIsPreSaleEnabledForLocation(location) {
    // Check if we have pre-sale product data
    if (!window.productsWithPreSale || typeof window.productsWithPreSale !== 'object' || Object.keys(window.productsWithPreSale).length === 0) {
        return false;
    }
    
    // Get currently selected variants
    let selectedVariants = window.selectedVariants || [];
    
    if (!selectedVariants || selectedVariants.length === 0) {
        return false;
    }
    
    // Iterate through products object to find current product
    for (let productId of Object.keys(window.productsWithPreSale)) {
        const product = window.productsWithPreSale[productId];
        
        // Check if this product has any of the selected variants
        let hasSelectedVariant = false;
        if (product.variantIds && Array.isArray(product.variantIds)) {
            hasSelectedVariant = product.variantIds.some(variantId => {
                let variantNum = parseInt(variantId);
                return selectedVariants.some(selectedId => parseInt(selectedId) === variantNum);
            });
        }
        
        // If this product has the selected variant, check its pre-sale status for the location
        if (hasSelectedVariant && product.preSaleStatus) {
            // The extension writes the legacy four by capitalized name and newer
            // locations by the metafield key minus '_pre_sale' (usually lowercased) —
            // try both, same as the extension's own reader.
            if (product.preSaleStatus[location] !== undefined) {
                return product.preSaleStatus[location] || false;
            }
            return product.preSaleStatus[String(location).toLowerCase()] || false;
        }
    }
    
    return false;
}



function findPostcodeLocation(userSelectedPostcode) {
    // P3 cutover: prefer the dynamic all-locations resolver (adds Adelaide etc.).
    // Returns null when no/partial consolidated config -> fall through to legacy.
    var dyn = window.fbResolvePostcode && window.fbResolvePostcode(userSelectedPostcode);
    if (dyn) return dyn;

    // Check if the required postcode arrays exist
    if (typeof brisbanePostcodes === 'undefined' || typeof melbournePostcodes === 'undefined' || 
        typeof SydneyPostcodes === 'undefined' || typeof RestofAustraliaPostcodes === 'undefined') {
        console.warn('Postcode arrays not found, using fallback logic');
        return "Sydney"; // Fallback to Sydney
    }

    // Extracting the Postcodes property from each object
    var brisbanePostcodesArray = brisbanePostcodes.Postcodes[0].split(',');
    var melbournePostcodesArray = melbournePostcodes.Postcodes[0].split(',');
    var sydneyPostcodesArray = SydneyPostcodes.Postcodes[0].split(',');
    var restOfAustraliaPostcodesArray = RestofAustraliaPostcodes.Postcodes[0].split(',');

    // Helper function to check if the postcode matches the pattern
    function matchesPattern(pattern, postcode) {
        if (pattern.endsWith('*')) {
            let prefix = pattern.slice(0, -1);
            return postcode.startsWith(prefix);
        } else if (pattern.includes(',')) {
            return pattern.split(',').includes(postcode);
        } else {
            return pattern === postcode;
        }
    }

    // Convert userSelectedPostcode to string to ensure .startsWith works correctly
    let postcodeStr = userSelectedPostcode.toString();

    // Check each array for a match
    if (brisbanePostcodesArray.some(pattern => matchesPattern(pattern, postcodeStr))) {
        return "Brisbane";
    } else if (melbournePostcodesArray.some(pattern => matchesPattern(pattern, postcodeStr))) {
        return "Melbourne";
    } else if (sydneyPostcodesArray.some(pattern => matchesPattern(pattern, postcodeStr))) {
        return "Sydney";
    } else if (restOfAustraliaPostcodesArray.some(pattern => matchesPattern(pattern, postcodeStr))) {
        return "RestofAustralia";
    } else {
        return "Unknown Location"; // In case the postcode doesn't match any pattern
    }
}

function addLocationToUrl(location) {
    const baseUrl = window.location.href.split('?')[0];
    let newQueryString = '';

  // P5: build the filter query from the app's config first — window.fbLocations
  // carries each location's numeric Shopify id, so a new city needs no edit here.
  // The hardcoded five stay as the fallback for a store whose config hasn't been
  // regenerated, and an unknown location still returns the bare URL as before.
  const FB_STATIC_FILTERS = {
    'Brisbane': '70541672681',
    'Melbourne': '70507725033',
    'Sydney': '70541639913',
    'RestofAustralia': '74290069737',
    'Adelaide': '90760315113'
  };
  let fbLocId = (typeof window.fbLocationId === 'function' && window.fbLocationId(location)) || FB_STATIC_FILTERS[location];
  if (!fbLocId) {
    return baseUrl;
  }
  newQueryString = '?filter.v.m.location.' + fbLocId + '=1&filter.v.availability=1';

    return `${baseUrl}${newQueryString}`;
}

// Date picker helper functions (adapted from delivery-date-extension.js)
function popupGetAustraliaDate(timezone) {
    return moment.tz(timezone || 'Australia/Sydney');
}

function popupCreateAustraliaDate(dateStr, timezone) {
    return moment.tz(dateStr, timezone || 'Australia/Sydney');
}

function popupGetProductUnavailabilityDates(location) {
    if (!window.productsWithDateRestrictions || typeof window.productsWithDateRestrictions !== 'object' || Object.keys(window.productsWithDateRestrictions).length === 0) {
        return [];
    }
    
    let selectedVariants = window.selectedVariants || [];
    
    if (!selectedVariants || selectedVariants.length === 0) {
        return [];
    }
    
    let locationKey = location + '_unavailable_dates';
    let unavailableDates = [];
    
    Object.keys(window.productsWithDateRestrictions).forEach(productId => {
        const product = window.productsWithDateRestrictions[productId];
        
        let hasSelectedVariant = false;
        if (product.variantIds && Array.isArray(product.variantIds)) {
            hasSelectedVariant = product.variantIds.some(variantId => {
                let variantNum = parseInt(variantId);
                let isMatch = selectedVariants.some(selectedId => parseInt(selectedId) === variantNum);
                return isMatch;
            });
        }
        
        if (hasSelectedVariant && product.dateRestrictions) {
            if (product.dateRestrictions[locationKey]) {
                let productUnavailableDates = product.dateRestrictions[locationKey];
                if (Array.isArray(productUnavailableDates)) {
                    unavailableDates = unavailableDates.concat(productUnavailableDates);
                }
            }
        }
    });
    
    unavailableDates = [...new Set(unavailableDates)];
    return unavailableDates;
}

function popupGetCombinedBlockedDates(datePickerSettings, location) {
    if (typeof window.mainORSelectedVariant !== 'undefined' && window.mainORSelectedVariant) {
        if (typeof window.selectedVariants === 'undefined') {
            window.selectedVariants = [];
        }
        if (!window.selectedVariants.includes(window.mainORSelectedVariant)) {
            window.selectedVariants.push(window.mainORSelectedVariant);
        }
    }
    
    var appBlockedDates = datePickerSettings.selectedBlockedDates || [];
    var productUnavailableDates = popupGetProductUnavailabilityDates(location);
    if (window.fbCentrepieceLeadTime) {
        const pending = window.pendingDeliveryAdd;
        const productId = pending && pending.getProductId ? pending.getProductId() : (window.fbCentrepieceConfig || {}).pageProductId;
        productUnavailableDates = productUnavailableDates.concat(window.fbCentrepieceLeadTime.blockedDates(productId, location));
        ((window.fbCentrepieceConfig || {}).cartProductIds || []).forEach(function(id) {
            if (window.fbCentrepieceLeadTime.isProduct(id)) productUnavailableDates = productUnavailableDates.concat(window.fbCentrepieceLeadTime.blockedDates(id, location));
        });
    }
    
    var combinedDates = [...new Set([...appBlockedDates, ...productUnavailableDates])];
    
    return combinedDates;
}

// ========================================
// PRODUCT DELIVERY SETTINGS OVERRIDE FUNCTIONS
// (Ported from delivery-date-extension.js for popup)
// ========================================

// Check if user's postcode matches product-specific postcodes
function popupMatchesProductSpecificPostcode(userPostcode, productPostcodesArray) {
    if (!productPostcodesArray || !Array.isArray(productPostcodesArray) || productPostcodesArray.length === 0) {
        return false;
    }
    var postcodeStr = userPostcode.toString();
    return productPostcodesArray.some(function(pattern) { return popupMatchesPattern(pattern, postcodeStr); });
}

// Get applicable postcodes (product-specific or default location)
function popupGetApplicablePostcodes(location, userPostcode) {
    if (!window.productsWithDeliverySettings || typeof window.productsWithDeliverySettings !== 'object' || Object.keys(window.productsWithDeliverySettings).length === 0) {
        return 'default';
    }

    var selectedVariants = window.selectedVariants || [];
    if (!selectedVariants || selectedVariants.length === 0) {
        return 'default';
    }

    var productIds = Object.keys(window.productsWithDeliverySettings);
    for (var i = 0; i < productIds.length; i++) {
        var productId = productIds[i];
        var productData = window.productsWithDeliverySettings[productId];

        var hasMatchingVariant = selectedVariants.some(function(variantId) {
            return productData.variantIds && productData.variantIds.includes(parseInt(variantId));
        });

        if (hasMatchingVariant && productData.postcodes) {
            var locationKey = location + '_postcodes';
            var productSpecificPostcodes = productData.postcodes[locationKey];

            if (productSpecificPostcodes && productSpecificPostcodes.Postcodes && productSpecificPostcodes.Postcodes.length > 0) {
                var allPostcodes = [];
                productSpecificPostcodes.Postcodes.forEach(function(item) {
                    if (typeof item === 'string' && item.includes(',')) {
                        allPostcodes = allPostcodes.concat(item.split(',').map(function(p) { return p.trim(); }));
                    } else {
                        allPostcodes.push(item);
                    }
                });

                if (popupMatchesProductSpecificPostcode(userPostcode, allPostcodes)) {
                    return 'product-specific';
                } else {
                    return 'no-match';
                }
            } else {
                return 'default';
            }
        }
    }

    return 'default';
}

// Check if delivery settings override is enabled for the current product and location
function popupHasDeliverySettingsOverride(location, userPostcode) {
    if (!window.productsWithDeliverySettings || typeof window.productsWithDeliverySettings !== 'object' || Object.keys(window.productsWithDeliverySettings).length === 0) {
        return false;
    }

    var selectedVariants = window.selectedVariants || [];
    if (!selectedVariants || selectedVariants.length === 0) {
        return false;
    }

    // Use param first, then global (NOT cookie — cookie may not be set yet on first selection)
    if (!userPostcode) {
        userPostcode = popupSelectedPcode;
        if (!userPostcode) {
            return false;
        }
    }

    var productIds = Object.keys(window.productsWithDeliverySettings);
    for (var i = 0; i < productIds.length; i++) {
        var productId = productIds[i];
        var productData = window.productsWithDeliverySettings[productId];

        var hasMatchingVariant = selectedVariants.some(function(variantId) {
            return productData.variantIds && productData.variantIds.includes(parseInt(variantId));
        });

        if (hasMatchingVariant) {
            var deliverySettings = productData.deliverySettings && productData.deliverySettings[location];

            if (deliverySettings && deliverySettings.DeliverySettings && deliverySettings.DeliverySettings.length > 0) {
                var postcodeApplicability = popupGetApplicablePostcodes(location, userPostcode);

                if (postcodeApplicability === 'product-specific' || postcodeApplicability === 'default') {
                    return deliverySettings.DeliverySettings[0];
                }
            }
        }
    }

    return false;
}

// Check if manually block dates is enabled for the current product and location
function popupIsManuallyBlockDatesEnabled(location, userPostcode) {
    if (!window.productsWithDeliverySettings || typeof window.productsWithDeliverySettings !== 'object' || Object.keys(window.productsWithDeliverySettings).length === 0) {
        return false;
    }

    var selectedVariants = window.selectedVariants || [];
    if (!selectedVariants || selectedVariants.length === 0) {
        return false;
    }

    if (!userPostcode) {
        userPostcode = popupSelectedPcode;
        if (!userPostcode) {
            return false;
        }
    }

    var productIds = Object.keys(window.productsWithDeliverySettings);
    for (var i = 0; i < productIds.length; i++) {
        var productId = productIds[i];
        var productData = window.productsWithDeliverySettings[productId];

        var hasMatchingVariant = selectedVariants.some(function(variantId) {
            return productData.variantIds && productData.variantIds.includes(parseInt(variantId));
        });

        if (hasMatchingVariant) {
            var postcodeApplicability = popupGetApplicablePostcodes(location, userPostcode);

            if (postcodeApplicability === 'product-specific' || postcodeApplicability === 'default') {
                var metafieldKey = location.toLowerCase() + '.manually_block_dates';
                var manuallyBlockDates = productData.manuallyBlockDates && productData.manuallyBlockDates[metafieldKey];
                return manuallyBlockDates === 'true' || manuallyBlockDates === true;
            }
        }
    }

    return false;
}

// Get combined delivery settings (override merged with default fallback)
// Uses spread merge so ALL required properties are guaranteed to exist
function popupGetDeliverySettingsForLocation(location, userPostcode) {
    if (!userPostcode) {
        userPostcode = popupSelectedPcode;
    }

    var overrideSettings = popupHasDeliverySettingsOverride(location, userPostcode);

    if (overrideSettings) {
        // Get base location settings
        var baseSettings;
        // P5: one resolver for ALL locations — prefers the app's per-location config and
        // falls back to the legacy four internally. Replaces a 4-city switch whose `default`
        // set null, which is why a 5th location (Adelaide) silently dropped to the generic
        // defaults below instead of using its own timezone / cutoff / working days. The popup
        // already depended on the extension for the {City}DatePickerArray globals, so this
        // adds no new dependency.
        baseSettings = (typeof window.fbGetLocationDateSettings === 'function')
            ? window.fbGetLocationDateSettings(location)
            : null;

        // Deploy-order safety net: the resolver above lives in the app extension. Until that
        // extension is deployed it is undefined, and WITHOUT this a per-product override would
        // stop merging for the legacy four. Read the per-city globals exactly as the pre-P5
        // switch did, so the theme is safe to ship in either order.
        if (!baseSettings) {
            var fbLegacyBase = window[location + 'DatePickerArray'];
            if (fbLegacyBase && fbLegacyBase.DatePickerSettings) {
                baseSettings = fbLegacyBase.DatePickerSettings[0] || null;
            }
        }

        if (!baseSettings) {
            return null;
        }

        // Spread merge: base has ALL properties, override only overwrites what it has
        var mergedSettings = Object.assign({}, baseSettings, {
            minDeliveryDays: overrideSettings.minDeliveryDays || baseSettings.minDeliveryDays,
            maxDeliveryDays: overrideSettings.maxDeliveryDays || baseSettings.maxDeliveryDays,
            activeCutoff: typeof overrideSettings.activeCutoff !== 'undefined' ? overrideSettings.activeCutoff : baseSettings.activeCutoff,
            cutoffTime: overrideSettings.cutoffTime || baseSettings.cutoffTime,
            recurringUnavailableDays: overrideSettings.recurringUnavailableDays || baseSettings.recurringUnavailableDays
        });

        return mergedSettings;
    }

    // No override — return null so caller falls through to default switch
    return null;
}

// Get the limited set of available delivery dates for override products
function popupGetAvailableDeliveryDates(datePickerSettings, timezone, minDateAdjustment, location, userPostcode) {
    var dayMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
    var nowInAustraliaTimezone = popupGetAustraliaDate(timezone);

    var nowComponents = nowInAustraliaTimezone.toArray();
    var now = new Date(nowComponents[0], nowComponents[1], nowComponents[2]);

    var minDeliveryDaysInt = parseInt(datePickerSettings.minDeliveryDays);
    var earliestDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + minDeliveryDaysInt + minDateAdjustment);

    var hasOverride = popupHasDeliverySettingsOverride(location, userPostcode);

    var unavailableDayNumbers = (datePickerSettings.recurringUnavailableDays || []).map(function(day) { return dayMap[day]; });
    var availableDayNumbers = [0, 1, 2, 3, 4, 5, 6].filter(function(day) { return !unavailableDayNumbers.includes(day); });

    if (availableDayNumbers.length === 0) {
        return [];
    }

    var maxDatesToShow;
    if (hasOverride) {
        maxDatesToShow = availableDayNumbers.length === 1 ? 1 : 2;
    } else {
        maxDatesToShow = parseInt(datePickerSettings.maxDeliveryDays) - minDeliveryDaysInt + 1;
    }

    var availableDates = [];
    var checkDate = new Date(earliestDate);
    var maxDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + parseInt(datePickerSettings.maxDeliveryDays));

    while (checkDate <= maxDate && availableDates.length < maxDatesToShow) {
        if (availableDayNumbers.includes(checkDate.getDay())) {
            var checkMoment = moment.tz(checkDate, timezone);
            var checkFormatted = checkMoment.format('YYYY-MM-DD');

            var isAppBlocked = (datePickerSettings.selectedBlockedDates || []).some(function(blockedDate) {
                var blockedMoment = popupCreateAustraliaDate(blockedDate, timezone);
                return blockedMoment.format('YYYY-MM-DD') === checkFormatted;
            });

            var productUnavailableDates = popupGetProductUnavailabilityDates(location);
            var isProductUnavailable = productUnavailableDates.some(function(unavailableDate) {
                var unavailableMoment = popupCreateAustraliaDate(unavailableDate, timezone);
                return unavailableMoment.format('YYYY-MM-DD') === checkFormatted;
            });

            if (!isAppBlocked && !isProductUnavailable) {
                availableDates.push(new Date(checkDate));
            }
        }
        checkDate.setDate(checkDate.getDate() + 1);
    }

    return availableDates;
}

// ========================================
// END OVERRIDE FUNCTIONS
// ========================================

function popupCanSelectDate(date) {
    if (!popupFlatpickr) return false;
    return !popupFlatpickr.config.disable.some(disableFunction => disableFunction(date)) && 
           date >= popupFlatpickr.config.minDate && 
           date <= popupFlatpickr.config.maxDate;
}

// Step transition functions - NO transitions
function showStep(stepId) {
    const steps = ['locationStep', 'dateStep', 'calendarStep', 'pickupTimeStep'];
    
    steps.forEach(id => {
        const step = document.getElementById(id);
        if (id === stepId) {
            step.classList.add('active');
            step.style.display = 'block';
        } else {
            step.classList.remove('active');
            step.style.display = 'none';
        }
    });
}

// ========================================
// CLICK & COLLECT: PICKUP TIME
// ========================================
// The slots and the "nothing sooner than an hour from now" rule live in
// assets/fb-pickup-time.js (window.fbPickupTime). This step draws them, and
// hands the chosen one back to popupFinishSelection(), which saves it.

// The studio's current time, for the same-day rule. popupDatePickerSettings.timezone is
// the studio city's zone, so Sydney, Melbourne and Brisbane each use their own clock.
function popupPickupNow() {
    const nowMoment = popupGetAustraliaDate(popupDatePickerSettings && popupDatePickerSettings.timezone);
    return {
        ymd: nowMoment.format('YYYY-MM-DD'),
        minutes: nowMoment.hours() * 60 + nowMoment.minutes()
    };
}

function showPickupTimeStep(dateStr, isoDate) {
    popupSelectedDateStr = dateStr;
    popupSelectedISODate = isoDate;

    const dateText = document.getElementById('pickupSelectedDateText');
    if (dateText) {
        dateText.textContent = dateStr;
    }

    // isoDate is "2026-10-05T00:00:00+11:00": its first ten characters are the date
    // the customer picked, in the studio's own zone.
    const slots = window.fbPickupTime
        ? window.fbPickupTime.slotsFor(String(isoDate).slice(0, 10), popupPickupNow())
        : [];

    const list = document.getElementById('pickupTimeSlots');
    const none = document.getElementById('pickupTimeNone');
    if (list) {
        list.textContent = '';
        slots.forEach(function(slot) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'popup-pickup-slot';
            btn.setAttribute('data-time', slot.value);
            btn.textContent = slot.label;
            btn.addEventListener('click', function() {
                selectPickupTime(slot.value);
            });
            list.appendChild(btn);
        });
        list.style.display = slots.length ? '' : 'none';
    }
    if (none) {
        none.style.display = slots.length ? 'none' : 'block';
    }

    showStep('pickupTimeStep');

    // Same as the date step: the step that held focus is now hidden, so land it on
    // this step's heading rather than dropping it to <body>.
    const heading = document.getElementById('pickupTimeHeading');
    if (heading) {
        requestAnimationFrame(function() {
            heading.focus();
        });
    }
}

function selectPickupTime(timeValue) {
    // One tap picks the time and finishes. Stop a
    // double tap from finishing twice.
    document.querySelectorAll('.popup-pickup-slot').forEach(function(slot) {
        slot.disabled = true;
    });
    popupFinishSelection(popupSelectedDateStr, popupSelectedISODate, timeValue);
}

// Back and Edit both return to the date step (Today / Select Delivery Date), so the customer
// can pick the same date again or a different one.
function showDateFromPickupTimeStep() {
    // Un-select the date, so choosing the same date again registers as a change.
    // clear() fires onChange with no dates, which the handler ignores.
    if (popupFlatpickr) {
        popupFlatpickr.clear();
    }
    showStep('dateStep');
}

function fbClearPendingAddNotice() {
    var n = document.getElementById('fbPendingAddUnavailable');
    if (n) n.remove();
}

function fbPendingAddCityCheck(locality, pcode) {
    fbClearPendingAddNotice();
    try {
        var pending = window.pendingDeliveryAdd;
        if (!pending || typeof pending.cityUnavailableFor !== 'function') return;
        if (!pending.cityUnavailableFor(pcode)) return;
        if (typeof pending.trackQuickAddBlocked === 'function') {
            pending.trackQuickAddBlocked('city_unavailable', { stage: 'location_step', postcode_city: findPostcodeLocation(pcode) });
        }
        window.pendingDeliveryAdd = null;
        var step = document.getElementById('dateStep');
        if (!step) return;
        var note = document.createElement('p');
        note.id = 'fbPendingAddUnavailable';
        note.setAttribute('role', 'status');
        note.textContent = "Sorry, that item isn't available for delivery to " + (locality || 'this suburb') + ". Choose a date and we'll show you everything we can deliver there.";
        note.style.cssText = 'margin:0 0 12px;padding:10px 12px;border-radius:6px;background:#fdf1ec;color:#7a2e12;font-size:14px;line-height:1.4;text-align:center;';
        step.insertBefore(note, step.firstChild);
    } catch (err) { /* the notice is best-effort */ }
}

function popupSetupDatePicker(locality, state, pcode) {
    // If this is a pre-sale, redirect to pre-sale setup
    if (window.pendingPreSaleForm) {
        popupSetupDatePickerForPreSale(locality, state, pcode);
        return;
    }

    // When the date picker is NOT opened for a specific product add (e.g. the header
    // "edit date" pill — a general delivery-date change), don't carry over any
    // product-specific blocked dates. They're registered per-add and, because our
    // in-place filtering doesn't reload the page, persist in window.selectedVariants /
    // mainORSelectedVariant. Clearing them here (only when there's no pending add) lets
    // the general date picker show every generally-available date, incl. today.
    // EXCEPTION — never clear on product pages (/products/*): there the Delivery Date App
    // (delivery-date-extension.js) OWNS these globals and builds its add-to-cart payload
    // from them (processCartAdditionEnhanced maps over window.selectedVariants, with no
    // fallback). Clearing them on a PDP leaves the app an empty payload, so "Add to Cart"
    // silently adds nothing until the variant is changed. The header "edit date" pill only
    // exists on collection/index pages, so skipping the clear on PDPs is safe.
    if (!window.pendingDeliveryAdd && !window.location.pathname.startsWith('/products/')) {
        window.selectedVariants = [];
        window.mainORSelectedVariant = null;
    }

    popupSelectedLocality = locality;
    popupSelectedState = state;
    popupSelectedPcode = pcode;

    // A quick add that is waiting on these details for a product that does not go
    // to this suburb used to ask for a date anyway, then drop the add at the city
    // gate in product-form.js: a 4-second toast, the card vanished from the
    // re-scoped grid, and nothing in the cart. That was most of the "picked a date,
    // nothing happened" quick adds. Say so now, before the date, and let the date
    // pick just scope the grid instead of resuming an add that cannot succeed.
    fbPendingAddCityCheck(locality, pcode);

    // Collect-vs-delivery for THIS setup. A studio selection must still match
    // the postcode being configured — otherwise this is a plain delivery setup
    // (e.g. reopened from cookies after the location changed elsewhere), and the
    // mode drops back to delivery so the finish step persists the truth.
    const isCollectSetup = popupCollectAvailable() && popupIsCollectSelection(pcode);
    if (!isCollectSetup && popupFulfilmentMethod === 'collect') {
        popupFulfilmentMethod = 'delivery';
    }
    const fbDateNoun = isCollectSetup ? 'Collection' : 'Delivery';

    // Hide autocomplete results
    const resultsContainer = document.getElementById('popupAutocompleteResults');
    resultsContainer.style.display = 'none';

    // Update the selected location display
    const selectedLocationText = document.getElementById('selectedLocationText');
    if (isCollectSetup) {
        selectedLocationText.textContent = `${popupSelectedStudio} Studio · Click & Collect`;
    } else {
        selectedLocationText.textContent = `${locality}, ${pcode}`;
    }

    // Wording: the date being chosen is a collection date when collecting. The
    // label now sits on the date button, because the heading above it is gone.
    // Clearing the status region on every setup also drops any stale
    // capacity-error message a previous open left there.
    const dateHeaderTextEl = document.querySelector('.popup-date-header p');
    if (dateHeaderTextEl) {
        dateHeaderTextEl.innerHTML = '';
    }
    const dateButtonEl = document.getElementById('popupSelectDate');
    if (dateButtonEl) {
        dateButtonEl.textContent = 'Choose date';
    }
    const calendarHeaderTextEl = document.querySelector('.calendar-header p');
    if (calendarHeaderTextEl) {
        calendarHeaderTextEl.innerHTML = '<strong>Choose Your ' + fbDateNoun + ' Date</strong>';
    }

    // Find location and get date picker settings
    popupCurrentLocation = findPostcodeLocation(pcode);

    // Try override settings first (product pages only)
    popupDatePickerSettings = null;
    if (isProductPage) {
        popupDatePickerSettings = popupGetDeliverySettingsForLocation(popupCurrentLocation, pcode);
    }

    // Fallback: default location settings (for non-product pages, or if override returned null)
    if (!popupDatePickerSettings) {
        // P5: one resolver for ALL locations — prefers the app's per-location config and
        // falls back to the legacy four internally. Replaces a 4-city switch whose `default`
        // set null, which is why a 5th location (Adelaide) silently dropped to the generic
        // defaults below instead of using its own timezone / cutoff / working days. The popup
        // already depended on the extension for the {City}DatePickerArray globals, so this
        // adds no new dependency.
        popupDatePickerSettings = (typeof window.fbGetLocationDateSettings === 'function')
            ? window.fbGetLocationDateSettings(popupCurrentLocation)
            : null;

        // Deploy-order safety net: the resolver above lives in the app extension. Until that
        // extension is deployed it is undefined, and WITHOUT this ALL FOUR paying cities would
        // fall to the generic defaults below — wrong days, wrong cutoff, and empty
        // selectedBlockedDates/specialDays (public holidays and closures stop being enforced).
        // Read the per-city globals exactly as the pre-P5 switch did.
        if (!popupDatePickerSettings) {
            var fbLegacyPopup = window[popupCurrentLocation + 'DatePickerArray'];
            if (fbLegacyPopup && fbLegacyPopup.DatePickerSettings) {
                popupDatePickerSettings = fbLegacyPopup.DatePickerSettings[0] || null;
            }
        }
    }

    // Create default settings if none found
    if (!popupDatePickerSettings) {
        console.warn('Date picker settings not found for location:', popupCurrentLocation, 'using defaults');
        popupDatePickerSettings = {
            timezone: 'Australia/Sydney',
            cutoffTime: '13:00',
            minDeliveryDays: 1,
            maxDeliveryDays: 14,
            workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            selectedBlockedDates: [],
            specialDays: [],
            firstDay: 'Monday',
            activeCutoff: true
        };
    }
    
    
   // Show cutoff time with clock icon (hide for RestofAustralia and override products)
const cutoffDisplay = document.getElementById('popupCutoffTimeDisplay');
const cutoffTimeText = document.getElementById('cutoffTimeText');
const hasOverrideForCutoff = isProductPage && popupHasDeliverySettingsOverride(popupCurrentLocation, pcode);
// No-cutoff locations (activeCutoff off -> cutoffTime null) get no banner, the
// same treatment RestofAustralia already had.
// "Order before X for same day" is only true where same-day delivery exists
// (minDeliveryDays 0). Adelaide (min 1, StarTrack) was showing it falsely; the
// old hardcoded RestofAustralia exclusion (min 2) is subsumed by this check.
if (getMinDeliveryDays(popupDatePickerSettings) === 0 && !hasOverrideForCutoff && popupDatePickerSettings.cutoffTime && popupDatePickerSettings.activeCutoff !== false) {
    // The banner is optional. A missing helper, a throw, or a null label must
    // hide it — never "Order before null", never 1:15 — and must not skip the
    // Today cutoff setup below, which still reads cutoffTime.
    let amPmCutoffTime = null;
    try {
        if (typeof fbCutoffDisplay !== 'undefined' && fbCutoffDisplay && typeof fbCutoffDisplay.label === 'function') {
            amPmCutoffTime = fbCutoffDisplay.label(popupDatePickerSettings);
        }
    } catch (err) {
        amPmCutoffTime = null;
    }
    if (amPmCutoffTime) {
        cutoffTimeText.textContent = `Order before ${amPmCutoffTime} for same day ${isCollectSetup ? 'collection' : 'delivery'}`;
        cutoffDisplay.style.display = 'flex';
    } else {
        if (cutoffTimeText) cutoffTimeText.textContent = '';
        cutoffDisplay.style.display = 'none';
    }
} else {
    cutoffDisplay.style.display = 'none';
}

// Courier expectations. Outside the Sydney/Melbourne/Brisbane studio cities
// (RestofAustralia, Adelaide, and any future fbResolvePostcode city) delivery
// is by StarTrack: the chosen date is a target, not a promise — arrival can be
// a business day either side, and a failed delivery is left at the recipient's
// post office. Say so at the moment the date is chosen, and soften the step
// headings to "Preferred Delivery Date". (Collect is always a studio city, so
// isCourierRegion can never be true for a collect setup; the guard is
// belt-and-braces.)
const isCourierRegion = ['Sydney', 'Melbourne', 'Brisbane'].indexOf(popupCurrentLocation) === -1;
const courierNotice = document.getElementById('popupCourierNotice');
if (courierNotice) {
    courierNotice.style.display = isCourierRegion ? 'flex' : 'none';
}
const courierNoticeCalendar = document.getElementById('popupCourierNoticeCalendar');
if (courierNoticeCalendar) {
    courierNoticeCalendar.style.display = isCourierRegion ? 'flex' : 'none';
}
if (isCourierRegion && !isCollectSetup) {
    if (dateButtonEl) {
        dateButtonEl.textContent = 'Select Preferred Delivery Date';
    }
    if (calendarHeaderTextEl) {
        calendarHeaderTextEl.innerHTML = '<strong>Choose Your Preferred Delivery Date</strong>';
    }
}
    
  // Setup date calculations
const dayMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
const nowInAustraliaTimezone = popupGetAustraliaDate(popupDatePickerSettings.timezone);
const now = nowInAustraliaTimezone.toDate(); // DON'T USE THIS - it converts back to local timezone

const timezone = popupDatePickerSettings.timezone || 'Australia/Sydney';
const nowInTargetTimeZone = moment.tz(timezone);

// Parse cutoff time. cutoffTime is null when the location's cutoff is switched
// off in the admin — treat that as "never past cutoff", which is also what
// activeCutoff:false already means one line down.
let isPastCutoff = false;
if (popupDatePickerSettings.cutoffTime) {
    const cutoffTimeParts = popupDatePickerSettings.cutoffTime.split(':');
    const cutoffDateTime = nowInTargetTimeZone.clone().set({
        hour: parseInt(cutoffTimeParts[0], 10),
        minute: parseInt(cutoffTimeParts[1], 10),
        second: 0,
        millisecond: 0
    });
    isPastCutoff = nowInTargetTimeZone.isAfter(cutoffDateTime);
}
const minDateAdjustment = (popupDatePickerSettings.activeCutoff && isPastCutoff) ? 1 : 0;

// FIXED: Use Australian timezone for all date calculations
const australianNow = moment.tz(timezone);
// Use the shared minimum delivery days.
const baseMinDeliveryDays = getMinDeliveryDays(popupDatePickerSettings);
const minDeliveryDays = baseMinDeliveryDays + minDateAdjustment;
const maxDeliveryDays = parseInt(popupDatePickerSettings.maxDeliveryDays);


// Create min and max dates in Australian timezone, then convert to local for flatpickr
const minDateAustralian = australianNow.clone().add(minDeliveryDays, 'days');
const maxDateAustralian = australianNow.clone().add(maxDeliveryDays, 'days');

// High Floral Design: two business days' notice, and the same-day cutoff plays no
// part. Applies when the add, the product page or the cart involves an HFD piece
// (assets/fb-hfd-lead-time.js), because a cart holding one has one date.
const hfdLeadTime = window.fbHfdLeadTime;
const pendingAddProductId = (window.pendingDeliveryAdd && typeof window.pendingDeliveryAdd.getProductId === 'function')
    ? window.pendingDeliveryAdd.getProductId()
    : null;
const hfdEarliestYmd = (hfdLeadTime && hfdLeadTime.applies(pendingAddProductId))
    ? hfdLeadTime.earliestForLocation(popupCurrentLocation)
    : null;
const hfdEarliestAustralian = hfdEarliestYmd ? moment.tz(hfdEarliestYmd, timezone) : null;
const hfdMovesMinDate = !!hfdEarliestAustralian && hfdEarliestAustralian.isAfter(minDateAustralian, 'day');
let effectiveMinAustralian = hfdMovesMinDate ? hfdEarliestAustralian : minDateAustralian;
const centrepieceLead = window.fbCentrepieceLeadTime;
const centrepieceEarliest = centrepieceLead && centrepieceLead.applies(pendingAddProductId)
    ? centrepieceLead.earliestForLocation(popupCurrentLocation) : null;
if (centrepieceEarliest && moment.tz(centrepieceEarliest, timezone).isAfter(effectiveMinAustralian, 'day')) {
    effectiveMinAustralian = moment.tz(centrepieceEarliest, timezone);
}

// Convert to local dates for flatpickr (but keep timezone logic separate)
const minDate = new Date(effectiveMinAustralian.year(), effectiveMinAustralian.month(), effectiveMinAustralian.date());
const maxDate = new Date(maxDateAustralian.year(), maxDateAustralian.month(), maxDateAustralian.date());


    
    // UPDATED: Setup Today button visibility WITH CAPACITY CHECK
    const todayButton = document.getElementById('popupSelectToday');
    const isTodayWorkingDay = popupDatePickerSettings.workingDays.map(day => dayMap[day]).includes(nowInAustraliaTimezone.day());
    const combinedBlockedDates = popupGetCombinedBlockedDates(popupDatePickerSettings, popupCurrentLocation);
    const isTodayBlocked = combinedBlockedDates.some(blockedDate => {
        const blockedMoment = popupCreateAustraliaDate(blockedDate, timezone);
        return blockedMoment.format('YYYY-MM-DD') === nowInAustraliaTimezone.format('YYYY-MM-DD');
    });
    
    // ADDED: Check capacity for today
    const isTodayCapacityReached = popupIsCapacityReachedForToday(popupCurrentLocation);
    
    // ADDED: Check app-level blocked dates for today
    const hasAppBlockedDatesToday = popupHasAppLevelBlockedDatesForToday(popupDatePickerSettings, timezone);
    
   // Include capacity and minimum days in today button visibility.
const effectiveMinDays = getMinDeliveryDays(popupDatePickerSettings) + minDateAdjustment;
const canDeliverToday = effectiveMinDays === 0 && !hfdEarliestYmd && !centrepieceEarliest;

// HFD replaces the "order before 1pm" line with its own notice. Written here,
// after the cutoff banner above, because it needs the HFD date.
if (hfdEarliestYmd && cutoffDisplay && cutoffTimeText) {
    cutoffTimeText.textContent = hfdLeadTime.notice(effectiveMinAustralian.format('YYYY-MM-DD'));
    cutoffDisplay.style.display = 'flex';
}

// Check if today falls on a recurring unavailable day
const isTodayRecurringUnavailable = popupDatePickerSettings.recurringUnavailableDays &&
    popupDatePickerSettings.recurringUnavailableDays.map(function(day) { return dayMap[day]; })
    .includes(nowInAustraliaTimezone.day());

if (isPastCutoff || !isTodayWorkingDay || isTodayBlocked || isTodayCapacityReached || isTodayRecurringUnavailable || popupCurrentLocation === "RestofAustralia" || !canDeliverToday) {
    todayButton.style.display = 'none';
} else {
    todayButton.style.display = 'inline-block';
}
    
    // Compute override state ONCE (before flatpickr init, used as closure vars in disable function)
    var hasOverride = false;
    var manuallyBlockEnabled = false;
    if (isProductPage) {
        hasOverride = popupHasDeliverySettingsOverride(popupCurrentLocation, pcode);
        manuallyBlockEnabled = popupIsManuallyBlockDatesEnabled(popupCurrentLocation, pcode);
    }

    // Initialize Flatpickr with inline mode
    if (popupFlatpickr) {
        popupFlatpickr.destroy();
    }
    // flatpickr.destroy() does not remove the temp input we created for it
    if (window.__popupFlatpickrTempInput && window.__popupFlatpickrTempInput.parentNode) {
        window.__popupFlatpickrTempInput.remove();
    }

    // Create a temporary input for flatpickr
    const tempInput = document.createElement('input');
    tempInput.type = 'text';
    tempInput.style.display = 'none';
    document.body.appendChild(tempInput);
    window.__popupFlatpickrTempInput = tempInput;

    popupFlatpickr = flatpickr(tempInput, {
        enableTime: false,
        dateFormat: "l, J F Y",
        minDate: minDate,
        maxDate: maxDate,
        inline: true,
        static: false,
        disable: [
            function(date) {
                const localYear = date.getFullYear();
                const localMonth = date.getMonth();
                const localDay = date.getDate();

                const dateMoment = moment.tz([localYear, localMonth, localDay], timezone);
                const dateFormatted = dateMoment.format('YYYY-MM-DD');

                // Always disable dates before minDate
                if (date < minDate) {
                    return true;
                }

                if (hasOverride && manuallyBlockEnabled) {
                    // ── Branch 1: Override + manuallyBlockDates enabled ──
                    // Only product unavailable dates and capacity block dates; everything else enabled
                    const productUnavailableDates = popupGetProductUnavailabilityDates(popupCurrentLocation);
                    const isProductUnavailable = productUnavailableDates.some(function(unavailableDate) {
                        const unavailableMoment = popupCreateAustraliaDate(unavailableDate, timezone);
                        return unavailableMoment.format('YYYY-MM-DD') === dateFormatted;
                    });
                    if (isProductUnavailable) {
                        return true;
                    }

                    const isCapacityReached = popupCheckDeliveryCapacity(popupCurrentLocation, dateFormatted);
                    if (isCapacityReached) {
                        return true;
                    }

                    return false; // Allow all other dates in manual block mode

                } else if (hasOverride) {
                    // ── Branch 2: Override + manuallyBlockDates disabled ──
                    // Limited dates via getAvailableDeliveryDates + recurring unavailable days
                    const availableDeliveryDates = popupGetAvailableDeliveryDates(popupDatePickerSettings, timezone, minDateAdjustment, popupCurrentLocation, pcode);

                    const isAvailableDeliveryDate = availableDeliveryDates.some(function(availableDate) {
                        const availableMoment = moment.tz(availableDate, timezone);
                        return availableMoment.format('YYYY-MM-DD') === dateFormatted;
                    });

                    // Product unavailable dates always take highest priority
                    const productUnavailableDates = popupGetProductUnavailabilityDates(popupCurrentLocation);
                    const isProductUnavailable = productUnavailableDates.some(function(unavailableDate) {
                        const unavailableMoment = popupCreateAustraliaDate(unavailableDate, timezone);
                        return unavailableMoment.format('YYYY-MM-DD') === dateFormatted;
                    });
                    if (isProductUnavailable) {
                        return true;
                    }

                    // Override recurring unavailable days (overrides even special days)
                    const isRecurringUnavailableDay = popupDatePickerSettings.recurringUnavailableDays &&
                        popupDatePickerSettings.recurringUnavailableDays.map(function(day) { return dayMap[day]; }).includes(dateMoment.day());
                    if (isRecurringUnavailableDay) {
                        return true;
                    }

                    // Capacity check
                    const isCapacityReached = popupCheckDeliveryCapacity(popupCurrentLocation, dateFormatted);
                    if (isCapacityReached) {
                        return true;
                    }

                    // App-level blocked dates
                    const appBlockedDates = popupDatePickerSettings.selectedBlockedDates || [];
                    const isAppBlocked = appBlockedDates.some(function(blockedDate) {
                        const blockedMoment = popupCreateAustraliaDate(blockedDate, timezone);
                        return blockedMoment.format('YYYY-MM-DD') === dateFormatted;
                    });
                    if (isAppBlocked) {
                        return true;
                    }

                    // Only allow dates in the available delivery set
                    return !isAvailableDeliveryDate;

                } else {
                    // ── Branch 3: Standard (no override) ──
                    const combinedBlockedDates = popupGetCombinedBlockedDates(popupDatePickerSettings, popupCurrentLocation);

                    const isBlocked = combinedBlockedDates.some(function(blockedDate) {
                        const blockedMoment = popupCreateAustraliaDate(blockedDate, timezone);
                        return blockedMoment.format('YYYY-MM-DD') === dateFormatted;
                    });

                    // Product unavailable dates always take highest priority
                    const productUnavailableDates = popupGetProductUnavailabilityDates(popupCurrentLocation);
                    const isProductUnavailable = productUnavailableDates.some(function(unavailableDate) {
                        const unavailableMoment = popupCreateAustraliaDate(unavailableDate, timezone);
                        return unavailableMoment.format('YYYY-MM-DD') === dateFormatted;
                    });
                    if (isProductUnavailable) {
                        return true;
                    }

                    // Recurring unavailable days (checks popupDatePickerSettings.recurringUnavailableDays)
                    const isRecurringUnavailableDay = popupDatePickerSettings.recurringUnavailableDays &&
                        popupDatePickerSettings.recurringUnavailableDays.map(function(day) { return dayMap[day]; }).includes(dateMoment.day());
                    if (isRecurringUnavailableDay) {
                        return true;
                    }

                    // Capacity check
                    const isCapacityReached = popupCheckDeliveryCapacity(popupCurrentLocation, dateFormatted);
                    if (isCapacityReached) {
                        return true;
                    }

                    const isSpecialDay = popupDatePickerSettings.specialDays && popupDatePickerSettings.specialDays.some(function(specialDate) {
                        const specialMoment = popupCreateAustraliaDate(specialDate, timezone);
                        return specialMoment.format('YYYY-MM-DD') === dateFormatted;
                    });

                    const isWorkingDay = popupDatePickerSettings.workingDays.map(function(day) { return dayMap[day]; }).includes(dateMoment.day());

                    // Special days override app-level blocking and working day restrictions (but not capacity, product unavailable, or recurring unavailable)
                    if (isSpecialDay) {
                        return false;
                    }

                    return isBlocked || !isWorkingDay;
                }
            }
        ],
        onChange: function(selectedDates, dateStr, instance) {
            if (selectedDates.length > 0) {
                const selectedMoment = moment(dateStr, "dddd, Do MMMM YYYY");
                const selectedISODate = moment.tz(selectedMoment.format('YYYY-MM-DD'), timezone).startOf('day').format();
                
                // Check if this is a pre-sale flow
                if (window.pendingPreSaleForm) {
                    popupFinishSelectionPreSale(dateStr, selectedISODate);
                } else {
                    popupFinishSelection(dateStr, selectedISODate);
                }
            }
        },
        locale: {
            firstDayOfWeek: dayMap[popupDatePickerSettings.firstDay] || 1,
        }
    });

    // The inline calendar mounts at the END of <body> (after the temp input) and is
    // only moved inside the popup by showCalendarStep. Hide it until then, or it
    // renders in normal document flow below the footer — visible now that popup
    // completion no longer reloads the page. showCalendarStep sets display:block.
    if (popupFlatpickr.calendarContainer) {
        popupFlatpickr.calendarContainer.style.display = 'none';
    }

    // Show the date selection step WITHOUT transition
    showStep('dateStep');

    // Keyboard/SR: picking a location (studio card or autocomplete item) hides
    // the step holding the focused control, which would drop focus to <body>
    // and eject keyboard users from the modal. Land it on the first VISIBLE date
    // button, so the step announces itself and a forward Tab reaches the other
    // one. Today is hidden for HFD items and past the cutoff, so it cannot be
    // the focus target unconditionally.
    var fbTodayBtn = document.getElementById('popupSelectToday');
    var fbDateBtn = document.getElementById('popupSelectDate');
    var fbDateStepFocus = (fbTodayBtn && fbTodayBtn.style.display !== 'none') ? fbTodayBtn : fbDateBtn;
    if (fbDateStepFocus) {
        requestAnimationFrame(function() {
            fbDateStepFocus.focus();
        });
    }

    // Periodic cutoff time re-check (every 15 seconds)
    if (window._popupCutoffInterval) clearInterval(window._popupCutoffInterval);
    window._popupCutoffInterval = setInterval(function() {
        if (!popupDatePickerSettings || !popupDatePickerSettings.activeCutoff || !popupDatePickerSettings.cutoffTime) return;
        var nowCheck = moment.tz(popupDatePickerSettings.timezone);
        var cutoffParts = popupDatePickerSettings.cutoffTime.split(':');
        var cutoffCheck = nowCheck.clone().set({ hour: parseInt(cutoffParts[0]), minute: parseInt(cutoffParts[1]), second: 0 });
        if (nowCheck.isAfter(cutoffCheck)) {
            var todayBtn = document.getElementById('popupSelectToday');
            if (todayBtn) todayBtn.style.display = 'none';
        }
    }, 15000);
}

function popupSelectTodayDate() {
    if (!popupDatePickerSettings) return;

    const timezone = popupDatePickerSettings.timezone;
    const todaysDateAustralia = popupGetAustraliaDate(timezone).format("dddd, Do MMMM YYYY");
    const ISODate = popupGetAustraliaDate(timezone).startOf('day').format();
    const todayFormatted = popupGetAustraliaDate(timezone).format('YYYY-MM-DD');

    // High Floral Design never goes same day. The button is hidden for it; this
    // covers a stale button.
    const hfdPendingProductId = (window.pendingDeliveryAdd && typeof window.pendingDeliveryAdd.getProductId === 'function')
        ? window.pendingDeliveryAdd.getProductId()
        : null;
    if (window.fbHfdLeadTime && window.fbHfdLeadTime.applies(hfdPendingProductId)) {
        const todayButtonHfd = document.getElementById('popupSelectToday');
        if (todayButtonHfd) {
            todayButtonHfd.style.display = 'none';
        }
        return;
    }

    if (window.fbCentrepieceLeadTime && window.fbCentrepieceLeadTime.invalidDate(ISODate, popupSelectedPcode, hfdPendingProductId)) return;

    // Block "Today" when it's a per-product unavailable date for this location.
    // (The calendar disable already handles this; the Today button previously only
    // checked capacity, so it could bypass a product's blocked date.)
    const todayProductUnavailable = popupGetProductUnavailabilityDates(popupCurrentLocation);
    if (Array.isArray(todayProductUnavailable) && todayProductUnavailable.indexOf(todayFormatted) !== -1) {
        const dateHeader = document.querySelector('.popup-date-header p');
        if (dateHeader) {
            dateHeader.innerHTML = '<strong style="color: #d9534f;">This item is not available for delivery today.</strong><br><span style="font-size: 14px; color: #666;">Please select another date.</span>';
        }
        const todayButton = document.getElementById('popupSelectToday');
        if (todayButton) {
            todayButton.style.display = 'none';
        }
        return;
    }

    // ADDED: Double-check capacity before selecting today
    const isTodayCapacityReached = popupIsCapacityReachedForToday(popupCurrentLocation);
    if (isTodayCapacityReached) {
        console.log('Cannot select today - capacity reached');
        return;
    }
    
    if (window.pendingPreSaleForm) {
        // For pre-sale, use pre-sale finish selection
        popupFinishSelectionPreSale(todaysDateAustralia, ISODate);
    } else {
        popupFinishSelection(todaysDateAustralia, ISODate);
    }
}

function popupOpenDatePicker() {
    if (popupFlatpickr) {
        // Clear any previous messages
        hidePreSaleCapacityMessage();

        // Show calendar step
        showStep('calendarStep');

        // Focus hand-off: the "Select Delivery Date" button that was activated is now
        // hidden with its step — land focus on the calendar heading
        const fbCalendarHeading = document.getElementById('popupCalendarHeaderText');
        if (fbCalendarHeading) {
            requestAnimationFrame(function() {
                fbCalendarHeading.focus();
            });
        }
        
        // Update header for pre-sale if needed
        if (window.pendingPreSaleForm) {
            const calendarHeader = document.querySelector('.calendar-header p');
            if (calendarHeader) {
                calendarHeader.innerHTML = '<strong>Select your Pre-Order delivery date and be among the first to receive it.</strong>';
            }
        }

        // Move the calendar to our container
        const calendarContainer = document.getElementById('calendarContainer');
        const calendar = popupFlatpickr.calendarContainer;
        
        if (calendar && calendarContainer) {
            // Clear any existing content
            calendarContainer.innerHTML = '';
            
            // Re-add the loading overlay
            const loadingOverlay = document.createElement('div');
            loadingOverlay.id = 'calendarLoadingOverlay';
            loadingOverlay.className = 'calendar-loading-overlay';
            loadingOverlay.innerHTML = '<div class="calendar-loading-spinner"></div>';
            calendarContainer.appendChild(loadingOverlay);
            
            // Move the calendar into our container
            calendarContainer.appendChild(calendar);
            
            // Style the calendar to remove the arrow and fit properly
            calendar.style.position = 'static';
            calendar.style.display = 'block';
            calendar.style.boxShadow = 'none';
            calendar.style.border = 'none';
            calendar.style.borderRadius = '8px';
            calendar.style.width = '100%';
            calendar.style.backgroundColor = 'transparent';
            
            // Hide the arrow if it exists
            const arrow = calendar.querySelector('.flatpickr-arrow');
            if (arrow) {
                arrow.style.display = 'none';
            }
        }
    }
}

function showCalendarBackToDateSelection() {
    // Show date selection step
    showStep('dateStep');

    // Focus hand-off: the calendar step (and its Back button) just hid, so land
    // focus back on the first visible date button (same rule as after a location
    // choice), keeping a forward Tab on the other one.
    const fbBackTodayBtn = document.getElementById('popupSelectToday');
    const fbBackDateBtn = document.getElementById('popupSelectDate');
    const fbDateHeading = (fbBackTodayBtn && fbBackTodayBtn.style.display !== 'none') ? fbBackTodayBtn : fbBackDateBtn;
    if (fbDateHeading) {
        requestAnimationFrame(function() {
            fbDateHeading.focus();
        });
    }
}

/* Superseded code. In the inline version this was held inside a Liquid comment tag,
   which is what kept it out of the page. This file is a .js asset and is not
   Liquid-rendered, so it has to be a JavaScript comment instead or the dead code
   below would start executing. */
/* function popupFinishSelection(dateStr, isoDate) {
    // Set cookies exactly as in delivery-date-extension.js
    setCookie('deliverySuburb', popupSelectedLocality, 14);
    setCookie('deliveryState', popupSelectedState, 14);
    setCookie('deliveryPostcode', popupSelectedPcode, 14);
    setCookie('DeliveryDate', dateStr, 7);
    setCookie('ISODate', isoDate, 7);
    
    // Update global variables for consistency with delivery-date-extension.js
    if (typeof window.User_Selected_Delivery_Suburb !== 'undefined') {
        window.User_Selected_Delivery_Suburb = popupSelectedLocality;
    }
    if (typeof window.User_Selected_Delivery_PostCode !== 'undefined') {
        window.User_Selected_Delivery_PostCode = popupSelectedPcode;
    }
    
    // Update header display if function exists
    if (typeof updateHeaderDeliveryDisplay === 'function') {
        updateHeaderDeliveryDisplay(popupSelectedLocality, popupSelectedState, popupSelectedPcode);
    }
    
    // Close popup
    closePopup();
    
    // Redirect if on collections page
    const isCollectionsPage = window.location.pathname.startsWith('/collections');
    if (isCollectionsPage) {
        const trimmedLocality = findPostcodeLocation(popupSelectedPcode);
        const newUrl = addLocationToUrl(trimmedLocality);
        if (newUrl !== window.location.href) {
            window.location.href = newUrl;
        }
    }
}  */

// STEP 4: Replace existing popupFinishSelection
function popupFinishSelection(dateStr, isoDate, pickupTime) {
    const centrepiece = window.fbCentrepieceLeadTime;
    const pending = window.pendingDeliveryAdd;
    const leadProductId = pending && pending.getProductId ? pending.getProductId() : (window.currentPreSaleProductId || null);
    if (centrepiece && centrepiece.invalidDate(isoDate, popupSelectedPcode, leadProductId)) {
        if (typeof window.popupShowUnavailableToast === 'function') window.popupShowUnavailableToast(null, 'Please choose an available delivery date.');
        return;
    }


    // A studio collection also needs an hour to come in. Nothing is saved until
    // the customer has picked one: showPickupTimeStep() calls back here with it.
    const isCollectFinish = popupIsCollectSelection(popupSelectedPcode);
    if (isCollectFinish && !pickupTime) {
        showPickupTimeStep(dateStr, isoDate);
        return;
    }

    // Set location cookies (shared across all pages)
    setCookie('deliverySuburb', popupSelectedLocality, 14);
    setCookie('deliveryState', popupSelectedState, 14);
    setCookie('deliveryPostcode', popupSelectedPcode, 14);

    // Set date cookies (for regular collections)
    setCookie('DeliveryDate', dateStr, 7);
    setCookie('ISODate', isoDate, 7);

    // Fulfilment method. A collection stores the studio's suburb/postcode as
    // the delivery location and the collection date as the delivery date, so
    // these two cookies are what distinguishes it (popup labels, header pills,
    // tab preselection on reopen).
    if (isCollectFinish) {
        setCookie('fulfilmentMethod', 'collect', 14);
        setCookie('collectStudio', popupSelectedStudio, 14);
        setCookie('pickupTime', pickupTime, 7);
    } else {
        setCookie('fulfilmentMethod', 'delivery', 14);
        clearCookie('collectStudio');
        clearCookie('pickupTime');
    }
    // Put the pickup time on the cart (or take it off, for a delivery) so it is
    // on the order whichever page the items are added from.
    if (window.fbPickupTime) {
        window.fbPickupTime.sync();
    }
    // A cart holding a High Floral Design piece has one date: move its other
    // lines onto the one just chosen (assets/fb-hfd-lead-time.js). A resumed add
    // does this itself once it lands, so only a plain date change calls it here
    // (running both at once could race the add's quantity).
    if (window.fbHfdLeadTime && !window.pendingDeliveryAdd) {
        window.fbHfdLeadTime.dateSaved();
    }
    // Came from "Choose a new delivery date" on the cart or drawer: move the
    // lines whose date has passed onto this one (assets/fb-cart-date-guard.js).
    if (window.fbCartDateGuard && typeof window.fbCartDateGuard.dateSaved === 'function' && !window.pendingDeliveryAdd) {
        window.fbCartDateGuard.dateSaved();
    }

    // Update global variables
    if (typeof window.User_Selected_Delivery_Suburb !== 'undefined') {
        window.User_Selected_Delivery_Suburb = popupSelectedLocality;
    }
    if (typeof window.User_Selected_Delivery_PostCode !== 'undefined') {
        window.User_Selected_Delivery_PostCode = popupSelectedPcode;
    }

    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'delivery_popup_complete',
            delivery_city: findPostcodeLocation(popupSelectedPcode),
            delivery_postcode: popupSelectedPcode,
            fulfilment_method: isCollectFinish ? 'collect' : 'delivery',
            collect_studio: isCollectFinish ? popupSelectedStudio : null
        });
    } catch (err) { /* analytics must never break the popup */ }
    fbPickerTrack('date', { method: isCollectFinish ? 'collect' : 'delivery' });

    // An add-to-cart may have been interrupted to collect delivery details — resume it.
    // Re-submitting the form re-runs ALL the normal gates (city availability, capacity),
    // exactly as if the user tapped Add to Cart again with cookies now set.
    fbClearPendingAddNotice();
    let pendingAdd = window.pendingDeliveryAdd;
    window.pendingDeliveryAdd = null;
    // The card the customer tapped can be replaced while the popup is open: the
    // first tap is what lazy-loads this file, and its initial-load scoping
    // (applyLocationFilterInPlace -> performLocationGridSwap) rewrites
    // #ProductGridContainer. The old form is then detached, document.body.contains
    // failed, and the add was dropped without a word. Re-find the same variant's
    // card in the current grid and resume on that one.
    if (pendingAdd && pendingAdd.form && !document.body.contains(pendingAdd.form)) {
        const variantId = pendingAdd.form.getAttribute('data-variant-id') ||
            (typeof pendingAdd.getVariantId === 'function' ? pendingAdd.getVariantId() : null);
        const fresh = variantId
            ? document.querySelector('product-form form.single-variant-form[data-variant-id="' + String(variantId).replace(/[^0-9]/g, '') + '"]')
            : null;
        const freshHost = fresh ? fresh.closest('product-form') : null;
        if (freshHost && freshHost.form === fresh) {
            pendingAdd = freshHost;
        } else {
            const unavailable = typeof pendingAdd.cityUnavailableFor === 'function' && pendingAdd.cityUnavailableFor(popupSelectedPcode);
            if (typeof pendingAdd.trackQuickAddBlocked === 'function') {
                pendingAdd.trackQuickAddBlocked(unavailable ? 'city_unavailable' : 'card_replaced', { stage: 'resume' });
            }
            if (typeof window.popupShowUnavailableToast === 'function') {
                window.popupShowUnavailableToast(popupSelectedLocality, unavailable
                    ? undefined
                    : "Your delivery details are saved. Please tap Add again to add it to your cart.", 7000);
            }
            pendingAdd = null;
        }
    }
    const canResumeAdd = pendingAdd && pendingAdd.form && document.body.contains(pendingAdd.form);
    function resumePendingAdd() {
        if (pendingAdd.form.requestSubmit) {
            pendingAdd.form.requestSubmit();
        } else {
            pendingAdd.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
    }

    // Determine if redirect needed
    const isCollectionsPage = window.location.pathname.startsWith('/collections');

    if (isCollectionsPage) {
        if (canResumeAdd) {
            // Resume the interrupted add FIRST; the grid re-scopes in place after the
            // cart responds (product-form.js consumes window.pendingPostAddRedirect).
            // updateHeaderDeliveryDisplay is deliberately NOT called here — it
            // dispatches deliveryInfoUpdated, which makes the header form hard-redirect
            // and would kill the in-flight add. The product-form.js hooks (and the
            // safety net below) refresh the header AFTER replaceState, when the
            // header's redirect listener can no longer fire.
            closePopup();
            window.pendingPostAddRedirect = true;
            setTimeout(function() {
                // Safety net: if the add never completes, still scope the grid and
                // refresh the header pills (safe: applyLocationFilterInPlace has
                // replaceState'd by then, so the header's redirect listener no-ops)
                if (window.pendingPostAddRedirect) {
                    window.pendingPostAddRedirect = false;
                    window.applyLocationFilterInPlace();
                    if (typeof updateHeaderDeliveryDisplay === 'function') {
                        updateHeaderDeliveryDisplay();
                    }
                }
            }, 8000);
            resumePendingAdd();
            return;
        }

        // Close popup, then scope the grid in place (no full-page redirect).
        // applyLocationFilterInPlace runs replaceState synchronously, so the
        // deliveryInfoUpdated event from updateHeaderDeliveryDisplay afterwards can
        // no longer trigger the header form's hard redirect.
        closePopup();
        window.applyLocationFilterInPlace();

        if (typeof updateHeaderDeliveryDisplay === 'function') {
            updateHeaderDeliveryDisplay();
        }
    } else {
        // Non-collections: just close and update header

        if (typeof updateHeaderDeliveryDisplay === 'function') {
            updateHeaderDeliveryDisplay();
        }

                closePopup();

        window.dispatchEvent(new CustomEvent('deliveryInfoUpdated'));

        if (canResumeAdd) {
            resumePendingAdd();
        }
    }
}

function resetToLocationStep() {
    // Don't reset to location step if this is a pre-sale flow
    if (window.pendingPreSaleForm) {
        return;
    }
    
    // Reset to location step
    showStep('locationStep');

    // Show the right fulfilment tab for the current state — "Edit" from a
    // studio selection lands back on the Click & Collect tab
    popupSetFulfilmentMethod(popupFulfilmentMethod);

    // Clear input field, and the no-match message that belonged to it
    const inputField = document.getElementById('popupDeliverySuburb');
    inputField.value = '';
    if (window.__clearPopupSuburbNoMatch) window.__clearPopupSuburbNoMatch();
    
    // Reset variables
    popupSelectedLocality = null;
    popupSelectedState = null;
    popupSelectedPcode = null;
    popupCurrentLocation = null;
    
    // Destroy flatpickr if exists
    if (popupFlatpickr) {
        popupFlatpickr.destroy();
        popupFlatpickr = null;
    }

    // Close button stays visible — the popup is always dismissible
    const closeBtn = document.getElementById('popupCloseBtn');
    if (closeBtn) {
        closeBtn.style.display = 'flex';
    }
}

function closePopup() {
    if (window.fbCartDateGuard && typeof window.fbCartDateGuard.endPopup === 'function') {
        window.fbCartDateGuard.endPopup();
    }
    fbClearPendingAddNotice();
    // Clear periodic cutoff check interval
    if (window._popupCutoffInterval) {
        clearInterval(window._popupCutoffInterval);
        window._popupCutoffInterval = null;
    }

    const popupModal = document.getElementById('popupModal');
    const popupBackground = document.getElementById('popupBackground');
    const mainContent = document.getElementById('MainContent');

    // Check if redirect is pending
    const isCollectionsPage = window.location.pathname.startsWith('/collections');
    const hasLocationCookies = getCookie('deliverySuburb') && getCookie('deliveryPostcode');
    const willRedirect = isCollectionsPage && hasLocationCookies && 
                         !window.RedirectManager.isRedirecting();
    
    // Hide popup elements
    if (popupModal) {
        popupModal.style.display = 'none';
    }
    if (popupBackground) {
        popupBackground.style.display = 'none';
        popupBackground.style.opacity = '0';
    }
    if (mainContent) {
        mainContent.classList.remove('blur-content');
    }
    
    // Restore scroll — overflow:hidden approach means page never moved,
    // so no need to scrollTo. Just remove the overflow locks.
    const body = document.body;
    body.style.overflow = '';
    document.documentElement.style.overflow = '';

    // iOS fix: restore sticky header if it was sticky before popup opened
    const stickyHeader = document.querySelector('.header__sticky');
    if (stickyHeader && stickyHeader.dataset.wasSticky === 'true') {
        stickyHeader.classList.add('sticky');
        delete stickyHeader.dataset.wasSticky;
    }
    
    clearPreSaleUI();
    
    
    // Only reset if NOT redirecting
    if (!willRedirect && !window.pendingPreSaleForm) {
        resetToLocationStep();
    }
    
    window.pendingPreSaleForm = null;
    window.currentPreSaleProductId = null;
}

function openPopupAtStep(targetStep) {
    if (typeof openPopup === 'function') {
        openPopup();
        
        setTimeout(function() {
            var locality = getCookie('deliverySuburb');
            var state = getCookie('deliveryState');
            var pcode = getCookie('deliveryPostcode');

            // Close button is always available
            const closeBtn = document.getElementById('popupCloseBtn');
            if (closeBtn) {
                closeBtn.style.display = 'flex';
            }

            if (targetStep === 'location' || (!locality || !pcode)) {
                // Reset to location step
                if (typeof resetToLocationStep === 'function') {
                    resetToLocationStep();

                    // Pre-fill if we have data (delivery only — a studio
                    // selection isn't meaningful text in the suburb field)
                    if (locality && pcode && popupFulfilmentMethod !== 'collect') {
                        var popupInput = document.getElementById('popupDeliverySuburb');
                        if (popupInput) {
                            popupInput.value = `${locality}, ${pcode}`;
                            var event = new Event('input', { bubbles: true });
                            popupInput.dispatchEvent(event);
                        }
                    }
                }
            } else if (targetStep === 'date' && locality && pcode) {
                // Check if we need to show capacity reached message
                if (window.showCapacityReachedMessage) {
                    window.showCapacityReachedMessage = false;
                    
                    setTimeout(function() {
                        const dateHeader = document.querySelector('.popup-date-header p');
                        if (dateHeader) {
                            dateHeader.innerHTML = '<strong style="color: #d9534f;">We have reached Delivery capacity for the selected Date.</strong><br><span style="font-size: 16px; color: #666;">Please select a new delivery date.</span>';
                        }
                    }, 200);
                }
                
                // Go directly to date selection
                if (window.pendingPreSaleForm) {
                    popupSetupPreSaleDatePicker(locality, state, pcode);
                } else {
                    popupSetupDatePicker(locality, state, pcode);
                }
            }
        }, 100);
    }
}



// Non-blocking notice shown when a product can't be delivered to the chosen suburb
// (used by product-form.js when a card/quick-view add fails the city availability gate)
window.popupShowUnavailableToast = function(suburb, customMessage, durationMs) {
    try {
        var existing = document.getElementById('fbDeliveryUnavailableToast');
        if (existing) existing.remove();
        var toast = document.createElement('div');
        toast.id = 'fbDeliveryUnavailableToast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.textContent = customMessage
            ? customMessage
            : (suburb
                ? "Sorry, this item isn't available for delivery to " + suburb + "."
                : "Sorry, this item isn't available for delivery to your area.");
        toast.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);background:#1f1f1f;color:#fff;padding:12px 18px;border-radius:8px;font-size:14px;line-height:1.4;z-index:2147483001;max-width:90vw;text-align:center;box-shadow:0 4px 16px rgba(0,0,0,0.25);';
        document.body.appendChild(toast);
        setTimeout(function() { toast.remove(); }, durationMs || 4000);
    } catch (err) { /* the notice is best-effort */ }
};

function clearPreSaleUI() {
    // Clear any capacity messages
    hidePreSaleCapacityMessage();
    
    // Hide loading overlay
    hidePreSaleCalendarLoading();
    
    // Drop any capacity / pre-order message from the date step. The step label
    // lives on the date button and is (re)set at setup, so leave it alone here.
    const dateHeader = document.querySelector('.popup-date-header p');
    if (dateHeader) {
        dateHeader.innerHTML = '';
    }
    
    // Clear stored date values
    popupSelectedDateStr = null;
    popupSelectedISODate = null;
    
    // Reset Today button text if it was changed for pre-sale
    const todayButton = document.getElementById('popupSelectToday');
    if (todayButton && todayButton.getAttribute('data-original-text')) {
        todayButton.textContent = todayButton.getAttribute('data-original-text');
        todayButton.removeAttribute('data-original-text');
    }
    
    // Reset calendar header
    const calendarHeader = document.querySelector('.calendar-header p');
    if (calendarHeader) {
        calendarHeader.innerHTML = '<strong>Choose Your Delivery Date</strong>';
    }
    
    // Show cutoff time display again (it gets hidden for pre-sale)
    const cutoffDisplay = document.getElementById('popupCutoffTimeDisplay');
    if (cutoffDisplay) {
        cutoffDisplay.style.display = 'flex';
    }
    
    // Show edit button again (it gets hidden for pre-sale)
    const editLocationBtn = document.getElementById('editLocationBtn');
    if (editLocationBtn) {
        editLocationBtn.style.display = 'inline-block';
    }
}


// Product-page PostHog picker events (assets/fb-delivery-picker-analytics.js, loaded on
// PDPs only by snippets/fb-posthog.liquid). Off the PDP there is no listener and this is a
// no-op. Analytics must never break the popup, hence the try/catch.
function fbPickerTrack(step, detail) {
    try {
        var a = window.fbDeliveryPickerAnalytics;
        if (a && typeof a.note === 'function') a.note(step, detail || {});
    } catch (err) { /* analytics must never break the popup */ }
}

function openPopup() {
    const popupModal = document.getElementById('popupModal');
    const popupBackground = document.getElementById('popupBackground');
    const mainContent = document.getElementById('MainContent');
    // Two submit listeners can both call openPopup for the same tap — only track the first
    const wasAlreadyOpen = popupModal && popupModal.style.display === 'block';

    // Start loading the deferred suburb-autocomplete dataset now, so it's ready by the
    // time the user reaches the suburb field (no-op if already loading/loaded).
    if (typeof window.__ensurePopupSuburbData === 'function') {
        window.__ensurePopupSuburbData();
    }

    // Make sure the fulfilment toggle reflects current state (auto-open and
    // first open land on the location step with no other render pass). Only
    // touches location-step elements, so it's harmless for date-step opens.
    popupSetFulfilmentMethod(popupFulfilmentMethod);

    // iOS fix: remove sticky header to prevent position:fixed stacking conflicts
    const stickyHeader = document.querySelector('.header__sticky');
    if (stickyHeader && stickyHeader.classList.contains('sticky')) {
        stickyHeader.dataset.wasSticky = 'true';
        stickyHeader.classList.remove('sticky');
    }

    popupModal.style.display = 'block';
    popupModal.style.position = 'fixed';
    popupModal.style.left = '0';

    if (popupBackground) {
        popupBackground.style.display = 'flex';
        popupBackground.style.opacity = '1';
    }

    if (mainContent) {
        mainContent.classList.add('blur-content');
    }

    // Scroll-lock: use overflow:hidden instead of body position:fixed.
    // The old approach (body position:fixed + top:-scrollY + height:100vh) caused
    // iOS white screen because the negative body offset creates a broken WebKit
    // compositing context, and 100vh !== visual viewport on iOS Safari.
    const body = document.body;
    body.dataset.popupScrollY = window.scrollY;
    body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

// Close button is always available — visitors must be able to dismiss and keep browsing
    const closeBtn = document.getElementById('popupCloseBtn');
    if (closeBtn) {
        closeBtn.style.display = 'flex';
    }

    if (!wasAlreadyOpen) {
        try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'delivery_popup_open',
                popup_trigger: window.__fbPopupTrigger || 'manual'
            });
        } catch (err) { /* analytics must never break the popup */ }
        fbPickerTrack('open', { trigger: window.__fbPopupTrigger || 'manual' });
    }
    window.__fbPopupTrigger = null;
}
/* Superseded code. In the inline version this was held inside a Liquid comment tag,
   which is what kept it out of the page. This file is a .js asset and is not
   Liquid-rendered, so it has to be a JavaScript comment instead or the dead code
   below would start executing. */
/* 
function updateHeaderDeliveryDisplay(locality, state, pcode) {
    // Attempt to retrieve the existing display element, or create a new one if it doesn't exist
    var deliveryDisplay = document.getElementById('headerdeliveryDisplay') || document.createElement('div');
    deliveryDisplay.id = 'headerdeliveryDisplay';
    deliveryDisplay.innerHTML = `<strong>${locality}, ${pcode}</strong> <span id="headereditDelivery" class="header-edit-button">Edit</span>`;

    // Attempt to retrieve the header delivery section where the display element will be placed
    var headerDeliverySection = document.querySelector('.header-delivery-section');

    // If the header delivery section exists and the delivery display is not already in place
    if (headerDeliverySection && !document.getElementById('headerdeliveryDisplay')) {
        // Append the delivery display to the header delivery section
        headerDeliverySection.appendChild(deliveryDisplay);
    } else if (headerDeliverySection) {
        // If the delivery display already exists, replace it to update the information
        var existingDisplay = document.getElementById('headerdeliveryDisplay');
        existingDisplay.replaceWith(deliveryDisplay);
    }
    
    var headerinputField = document.getElementById('headerDeliverySuburb');
    var headerresultsContainer = document.getElementById('headerAutocompleteResults');

    if (headerinputField) {
        headerinputField.style.display = 'none';
    }
    if (headerresultsContainer) {
        headerresultsContainer.style.display = 'none';
    }
    
    // Add event listener for the edit button
    document.getElementById('headereditDelivery').addEventListener('click', function() {
        if (headerinputField) {
            headerinputField.style.display = 'block';
            headerinputField.value = '';
            headerinputField.focus();
        }
        deliveryDisplay.remove();
        clearCookie('deliveryPostcode');
        clearCookie('deliverySuburb');
        clearCookie('deliveryState');
        clearCookie('DeliveryDate');
        clearCookie('ISODate');
    });

    const isCollectionsPage = window.location.pathname.startsWith('/collections');

    if (isCollectionsPage) {
        const trimmedLocality = findPostcodeLocation(pcode);
        console.log(trimmedLocality);
        const newUrl = addLocationToUrl(trimmedLocality);
        console.log(newUrl);
        if (newUrl !== window.location.href) {
            window.location.href = newUrl;
        }
    }
} */

  // Format delivery date for cleaner display
  function formatDeliveryDate(dateStr) {
    // If date is like "Wednesday, 27th November 2024", format it nicely
    if (dateStr.includes(',')) {
      var parts = dateStr.split(',');
      if (parts.length >= 2) {
        // Get day and date part
        var day = parts[0].trim();
        var datePart = parts[1].trim();
        // Remove year for cleaner display
        datePart = datePart.replace(/\s+\d{4}$/, '');
        // Return shortened format
        return `${day.substring(0, 3)}, ${datePart}`;
      }
    }
    return dateStr;
  }


// Find and replace the updateHeaderDeliveryDisplay function:

function updateHeaderDeliveryDisplay() {
    // Get cookie values
    var locality = getCookie('deliverySuburb');
    var state = getCookie('deliveryState');
    var pcode = getCookie('deliveryPostcode');
    // Use context-aware cookie getter for date
    var deliveryDate = getStoredDeliveryDate();
    
    
    
    // First, check if we're using the new header format
    var initialState = document.getElementById('headerInitialState');
    var displayState = document.getElementById('headerDisplayState');
    
    if (initialState && displayState) {
        // New header format exists - update it
        if (locality && pcode) {
            // Update location display — a studio collection shows the studio,
            // not its suburb/postcode
            var locationDisplay = document.getElementById('headerLocationDisplay');
            if (locationDisplay) {
                if (popupIsCollectSelection(pcode)) {
                    locationDisplay.textContent = `${popupSelectedStudio} Studio`;
                    locationDisplay.title = 'Click & Collect — ' + FB_COLLECT_STUDIOS[popupSelectedStudio].address;
                } else {
                    locationDisplay.textContent = `${locality}, ${pcode}`;
                    locationDisplay.title = `${locality}, ${pcode}`;
                }
            }
            
            // Update date display
            var dateDisplay = document.getElementById('headerDateDisplay');
            if (dateDisplay) {
                if (deliveryDate) {
                    // Format the date for display
                    var formattedDate = formatDeliveryDate(deliveryDate);
                    
                    dateDisplay.textContent = formattedDate;
                } else {
                    dateDisplay.textContent = 'Select Delivery Date';
                }
            }
            
            // Show display state, hide initial state
            initialState.style.display = 'none';
            displayState.style.display = 'flex';
        }
    } else {
        // Fall back to old header format for compatibility
        /* Superseded code. In the inline version this was held inside a Liquid comment tag,
   which is what kept it out of the page. This file is a .js asset and is not
   Liquid-rendered, so it has to be a JavaScript comment instead or the dead code
   below would start executing. */
/* updateOldHeaderFormat(locality, state, pcode); */
    }
    
    // Dispatch custom event for any listeners
    window.dispatchEvent(new CustomEvent('deliveryInfoUpdated', {
        detail: { locality, state, pcode, deliveryDate }
    }));
}


// Replace the existing DOMContentLoaded event listener with this improved version:

// Robust initialization that works in sandboxed environments (iOS Safari)
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
        setTimeout(initializePopup, 50);
    });
} else {
    setTimeout(initializePopup, 50);
}

// Backup: force init after 2 seconds no matter what
setTimeout(function() {
    if (!window.popupInitialized) {
        console.log('[INIT] Fallback initialization triggered');
        initializePopup();
    }
}, 2000);

function initializePopup() {
    // Prevent multiple initializations
    if (window.popupInitialized) {
        return;
    }
    
    // Mark as initialized
    window.popupInitialized = true;

    // Restore the remembered fulfilment method (delivery vs click & collect)
    // before anything renders labels or the header pills
    popupHydrateFulfilmentFromCookies();

    // Add a small delay to ensure all scripts have executed
    setTimeout(function() {
        checkAndShowPopup();
        setupPopupEventListeners();
    }, 100);
}


function showCapacityCheckLoading() {
    const dateButtons = document.querySelector('.popup-date-buttons');
    if (dateButtons) {
        dateButtons.style.opacity = '0.5';
        dateButtons.style.pointerEvents = 'none';
    }
}

function hideCapacityCheckLoading() {
    const dateButtons = document.querySelector('.popup-date-buttons');
    if (dateButtons) {
        dateButtons.style.opacity = '1';
        dateButtons.style.pointerEvents = 'auto';
    }
}

function checkAndShowPopup() {


    // Let RedirectManager handle redirects
    if (window.RedirectManager.checkAndRedirect()) {
        return; // Redirect is happening
    }
    
    const storedLocality = getCookie('deliverySuburb');
    const storedPcode = getCookie('deliveryPostcode');
    // Use context-aware cookie getters for date
    const storedDate = getStoredDeliveryDate();
    const storedISODate = getStoredISODate();
    const isCollectionsPage = window.location.pathname.startsWith('/collections');
    
    
    // Check if stored date has capacity reached (but location cookies exist)
    if (storedLocality && storedPcode && storedDate) {
        const location = findPostcodeLocation(storedPcode);
        
        // Extract date from stored ISO date
        let dateToCheck;
        if (storedISODate) {
            dateToCheck = storedISODate.split('T')[0];
        }
        
        if (dateToCheck && popupCheckDeliveryCapacity(location, dateToCheck)) {
            // Clear ONLY date cookies for current context (keep location cookies)
            clearCookie(getDeliveryDateCookieName());
            clearCookie(getISODateCookieName());
            
            if (popupAutoOpenEnabled && isCollectionsPage) {
                setTimeout(function() {
                    // Open popup at date selection step
                    window.__fbPopupTrigger = 'auto';
                    openPopupAtStep('date');
                    
                    // Add capacity reached message
                    setTimeout(function() {
                        const dateHeader = document.querySelector('.popup-date-header p');
                        if (dateHeader) {
                            dateHeader.innerHTML = '<strong style="color: #d9534f;font-size:14px;">We have reached Delivery capacity for the selected Date.</strong><br><br><span style="font-size: 14px; color: #666;">Please select a new delivery date.</span>';
                        }
                    }, 200);
                }, 200);
            } else {
                // Auto-open disabled: queue the capacity message for the next
                // user-initiated popup open (same flag the header form uses)
                window.showCapacityReachedMessage = true;
            }
            return;
        }
    }

    // Show popup if no location cookies and on collections page (gated by theme setting)
    if (popupAutoOpenEnabled && !storedLocality && !storedPcode && isCollectionsPage) {
        setTimeout(function() {
            window.__fbPopupTrigger = 'auto';
            openPopup();
        }, 200);
        return;
    }

    // Show the date step if the location exists but no date has been selected.
    if (popupAutoOpenEnabled && storedLocality && storedPcode && !storedDate && isCollectionsPage) {
        setTimeout(function() {
            window.__fbPopupTrigger = 'auto';
            openPopupAtStep('date');
        }, 200);
        return;
    }
    
}

function setupPopupEventListeners() {
    // Only set up event listeners once
    if (window.popupListenersSetup) {
        return;
    }
    window.popupListenersSetup = true;
    
    var suburbData = [];

    // The 1.2MB suburb-autocomplete dataset is only needed once the popup is open and
    // the user types in the suburb field. Fetch it lazily on first popup-open / field
    // focus instead of eagerly on every page load (it was previously downloaded on
    // DOMContentLoaded for every collection/product/home page even though most visitors
    // never open the popup). openPopup() and the focus/input listeners below trigger it.
    var suburbDataFetchStarted = false;
    // Until the file arrives, no match means "not loaded yet", not "we don't
    // deliver there", so the no-match message waits for this.
    var suburbDataLoaded = false;
    function ensureSuburbData() {
        if (suburbDataFetchStarted) return;
        suburbDataFetchStarted = true;
        fetch('https://cdn.shopify.com/s/files/1/0818/6209/9228/files/Postcodes.json')
            .then(response => response.json())
            .then(data => {
                suburbData = data;
                suburbDataLoaded = true;
                // Whatever was typed, or refilled by Edit location, while the file loaded
                // matched nothing and showed nothing: show its results now instead of
                // waiting for a keystroke, as long as the location step is still up.
                var activeStep = document.querySelector('.popup-step.active');
                if (inputField && inputField.value.trim() && (!activeStep || activeStep.id === 'locationStep')) {
                    showResults(inputField.value.trim());
                }
            })
            .catch(error => { suburbDataFetchStarted = false; console.error('Error loading postcode data:', error); });
    }
    window.__ensurePopupSuburbData = ensureSuburbData;

    var inputField = document.getElementById('popupDeliverySuburb');
    var resultsContainer = document.getElementById('popupAutocompleteResults');

    // Pcode is a number in the file, so SA's 0872 is stored as 872. Match on the four
    // digits people type; the raw value is still what's shown and sent on.
    function formatPcode(pcode) {
        return String(pcode).padStart(4, '0');
    }

    // The file spells out "Mount" (269 places) but abbreviates "St" (61), and people
    // type either. Try what was typed as it is, then with "Mt" spelled out, then with
    // "Saint" shortened too once a word follows it ("st" alone is inside hundreds of
    // names). So "Mt Waverley" finds Mount Waverley, "Saint Kilda" finds St Kilda, and
    // Mount Saint Thomas, the one place the file spells with Saint, still finds itself.
    // A half-typed "Moun" or a bare "Saint" matches just as it always did.
    function spellings(text) {
        var typed = String(text).toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();
        var mount = typed.replace(/\bmt\b/g, 'mount');
        return [typed, mount, mount.replace(/\bsaint /g, 'st ')];
    }

    // The Edit location button (openPopupAtStep here, and its copies in the header and
    // homepage forms) refills the field from the cookies as "Melbourne, 3000" and fires
    // an input event. Read that as the saved suburb, falling back to its postcode,
    // rather than as a search that matched nothing.
    var SAVED_LOCATION = /^(.+?)[,\s]+(\d{3,4})$/;

    // People type a suburb the way an address ends: "Richmond VIC", "Richmond, VIC",
    // "Richmond VIC 3121". No name in the file has a state in it, so that used to
    // match nothing and be told to check its spelling. When what was typed matches
    // nothing as it is, the state is read off the end: the suburb is searched within
    // that state, and a state the file has no suburbs in is one we don't deliver to.
    // Trying the text as typed first keeps "Mount Wa" on its way to Mount Waverley.
    var TRAILING_STATE = /^(.+?)[,\s]+(nsw|vic|qld|sa|act|nt|wa|tas)\.?$/i;
    var STATE_NAMES = {
        NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', SA: 'South Australia',
        ACT: 'the ACT', NT: 'the Northern Territory', WA: 'Western Australia', TAS: 'Tasmania'
    };

    function splitState(text) {
        var named = TRAILING_STATE.exec(String(text).trim());
        return named ? { place: named[1], state: named[2].toUpperCase() } : { place: text, state: null };
    }

    function stateServed(state) {
        return suburbData.some(item => String(item.State).toUpperCase() === state);
    }

    function search(text) {
        var places = spellings(text);
        return suburbData.filter(item => {
            var locality = item.Locality.toLowerCase();
            return places.some(place => locality.includes(place)) ||
                item.State.toLowerCase().includes(text.toLowerCase()) ||
                item.Pcode.toString().startsWith(text) ||
                formatPcode(item.Pcode).startsWith(text);
        });
    }

    function filterSuburbData(input) {
        var saved = SAVED_LOCATION.exec(input);
        if (saved) {
            var savedPcode = formatPcode(saved[2]);
            var savedPlaces = spellings(splitState(saved[1]).place);
            var samePcode = suburbData.filter(item => formatPcode(item.Pcode) === savedPcode);
            var samePlace = samePcode.filter(item => savedPlaces.includes(item.Locality.toLowerCase()));
            return (samePlace.length ? samePlace : samePcode).slice(0, 15);
        }
        var found = search(input);
        var named = splitState(input);
        if (found.length || !named.state) return found.slice(0, 15);
        // The suburb itself before the ones that contain its name: Richmond, then
        // Richmond East.
        var exact = spellings(named.place);
        return search(named.place)
            .filter(item => item.State.toUpperCase() === named.state)
            .sort((a, b) => exact.includes(b.Locality.toLowerCase()) - exact.includes(a.Locality.toLowerCase()))
            .slice(0, 15);
    }

    // A postcode, on its own or at the end ("Alice Springs 0870"), is what the file is
    // checked against, so a miss is answered about the postcode. One still being typed
    // ("08", "Alice Springs 087") gets no answer yet.
    var TYPED_POSTCODE = /(?:^|[,\s])(\d{4})$/;
    var PARTIAL_POSTCODE = /(?:^|[,\s])\d{1,3}$/;

    // Nothing matched: say so and offer a way on, instead of leaving the box silent.
    // The file holds every suburb this popup accepts (it has no NT, WA or TAS), so a
    // full postcode missing from it is one we don't deliver to, and so is a state it
    // has no suburbs in ("Alice Springs NT"). Any other text is a search that found
    // nothing (a typo, an abbreviation), and is worded as one.
    // Built with textContent, because `input` is whatever was typed.
    var noMatchContainer = document.getElementById('popupSuburbNoMatch');
    var noMatchStatus = noMatchContainer && noMatchContainer.querySelector('[role="status"]');
    var noMatchTimer = null;
    var noMatchShown = null;

    function clearNoMatch() {
        clearTimeout(noMatchTimer);
        noMatchShown = null;
        if (!noMatchContainer || !noMatchStatus) return;
        noMatchContainer.classList.remove('is-shown');
        noMatchStatus.textContent = '';
        var collectButton = noMatchContainer.querySelector('.popup-suburb-no-match__collect');
        if (collectButton) collectButton.remove();
    }
    // resetToLocationStep() empties the field from outside this function
    window.__clearPopupSuburbNoMatch = clearNoMatch;

    function showNoMatch(input) {
        if (!noMatchContainer || !noMatchStatus) return;
        var typedPostcode = TYPED_POSTCODE.exec(input);
        var postcode = typedPostcode ? typedPostcode[1] : null;
        var state = postcode ? null : splitState(input).state;
        var unserved = state && !stateServed(state) ? STATE_NAMES[state] : null;
        var studioCities = Object.keys(FB_COLLECT_STUDIOS);
        var canCollect = popupCollectAvailable() && studioCities.length > 0;
        var heading = postcode ? 'We don’t deliver to ' + postcode + '.'
            : unserved ? 'We don’t deliver to ' + unserved + '.'
            : 'No suburbs match ‘' + input + '’.';
        var hint = (postcode || unserved ? 'Try another suburb or postcode' : 'Check the spelling or try the postcode') +
            (canCollect ? ', or collect from one of our studios in ' + listCities(studioCities) : '') + '.';

        // The same words are already up: leave the live region alone, or a screen
        // reader reads them out again.
        if (noMatchShown === heading + hint) return;
        noMatchShown = heading + hint;

        noMatchStatus.textContent = '';
        var headingEl = document.createElement('p');
        // The heading repeats the typed suburb or postcode.
        headingEl.className = 'popup-suburb-no-match__heading ph-no-capture ph-sensitive';
        headingEl.textContent = heading;
        noMatchStatus.appendChild(headingEl);
        var hintEl = document.createElement('p');
        hintEl.textContent = hint;
        noMatchStatus.appendChild(hintEl);

        // The button sits beside the live region, not in it, so it isn't read out as
        // part of the message.
        var collectButton = noMatchContainer.querySelector('.popup-suburb-no-match__collect');
        if (canCollect && !collectButton) {
            collectButton = document.createElement('button');
            collectButton.type = 'button';
            collectButton.className = 'popup-suburb-no-match__collect';
            collectButton.textContent = 'Choose a studio';
            collectButton.addEventListener('click', function() {
                // The message stays with the delivery panel, as it does when the Click &
                // Collect tab is used, so it's still there on the way back.
                popupSetFulfilmentMethod('collect');
                var firstStudio = document.querySelector('.popup-studio-option');
                if (firstStudio) firstStudio.focus();
            });
            noMatchContainer.appendChild(collectButton);
        } else if (!canCollect && collectButton) {
            collectButton.remove();
        }
        noMatchContainer.classList.add('is-shown');
    }

    // ['Sydney', 'Melbourne', 'Brisbane'] -> 'Sydney, Melbourne or Brisbane'
    function listCities(cities) {
        if (cities.length < 2) return cities.join('');
        return cities.slice(0, -1).join(', ') + ' or ' + cities[cities.length - 1];
    }

    function showResults(input) {
        resultsContainer.innerHTML = '';
        clearTimeout(noMatchTimer);

        if (input) {
            let filteredData = filterSuburbData(input);
            filteredData.forEach(item => {
                let div = document.createElement('div');
                // Suburb and postcode text. ph-no-capture drops autocapture and
                // blocks the row in session replay; ph-sensitive is the text gate.
                div.className = 'popup-autocomplete-item ph-no-capture ph-sensitive';

                let spanLocalityState = document.createElement('span');
                spanLocalityState.textContent = `${item.Locality}, ${item.State}`;

                let spanPcode = document.createElement('span');
                spanPcode.textContent = item.Pcode;
                spanPcode.className = 'pcode';
                spanPcode.style.marginLeft = 'auto';

                div.appendChild(spanLocalityState);
                div.appendChild(spanPcode);

                div.addEventListener('click', () => {
                    // Picking a suburb is always the delivery path
                    popupFulfilmentMethod = 'delivery';
                    popupSelectedStudio = null;

                    // Setup date picker WITHOUT transition
                    popupSetupDatePicker(item.Locality, item.State, item.Pcode);
                    fbPickerTrack('suburb', { method: 'delivery' });
                });

                resultsContainer.appendChild(div);
            });

            resultsContainer.style.display = filteredData.length ? 'block' : 'none';
            if (filteredData.length || !suburbDataLoaded || PARTIAL_POSTCODE.test(input)) {
                // A match, the file not here yet, or a postcode still being typed:
                // nothing to say.
                clearNoMatch();
            } else {
                // Wait for a pause in typing, so a screen reader hears one message
                // rather than one per keystroke.
                noMatchTimer = setTimeout(function() { showNoMatch(input); }, 400);
            }
        } else {
            resultsContainer.style.display = 'none';
            clearNoMatch();
        }
    }

    // Set up scroll tracking
    window.addEventListener('scroll', () => {
        document.documentElement.style.setProperty('--scroll-y', `${window.scrollY}px`);
    });
    
document.getElementById('popupSelectToday').addEventListener('click', async function() {
    if (window.pendingPreSaleForm) {
        if (!popupDatePickerSettings) return;
        
        const timezone = popupDatePickerSettings.timezone;
        const todaysDateAustralia = popupGetAustraliaDate(timezone).format("dddd, Do MMMM YYYY");
        const ISODate = popupGetAustraliaDate(timezone).startOf('day').format();
        const todayDate = popupGetAustraliaDate(timezone).format('YYYY-MM-DD');
        
        // Show loading
        showCapacityCheckLoading();
        
        // Check capacity
        const isCapacityReached = await checkPreSaleCapacityWithAPI(popupCurrentLocation, todayDate);
        
        // Hide loading
        hideCapacityCheckLoading();
        
        if (isCapacityReached) {
            // Show message in date step (since Today button is on date step, not calendar step)
            const dateHeader = document.querySelector('.popup-date-header p');
            if (dateHeader) {
                dateHeader.innerHTML = '<strong style="color: #d9534f;">Today has reached delivery capacity</strong><br><span style="font-size: 14px; color: #666;">Please select another date.</span>';
            }
            
            // Optionally, hide the Today button after showing it's at capacity
            const todayButton = document.getElementById('popupSelectToday');
            if (todayButton) {
                todayButton.style.display = 'none';
            }
            
            return;
        }
        
        // If capacity is available, proceed with selection
        popupFinishSelectionPreSale(todaysDateAustralia, ISODate);
    } else {
        // Non-pre-sale flow
        popupSelectTodayDate();
    }
});

    document.getElementById('popupSelectDate').addEventListener('click', popupOpenDatePicker);
    
    // Event listener for calendar back button
    document.getElementById('calendarBackBtn').addEventListener('click', showCalendarBackToDateSelection);
    
    // Event listener for edit location button. Editing hides the date step (and
    // the Edit button itself), so focus must land on the matching control in
    // the location step or keyboard users fall out of the modal.
    document.getElementById('editLocationBtn').addEventListener('click', function() {
        resetToLocationStep();
        if (popupFulfilmentMethod === 'collect') {
            const studioTarget = document.querySelector('.popup-studio-option.selected')
                || document.querySelector('.popup-studio-option');
            if (studioTarget) {
                studioTarget.focus();
            }
        } else {
            const inputField = document.getElementById('popupDeliverySuburb');
            if (inputField) {
                inputField.focus();
            }
        }
    });

    // Fulfilment toggle (Delivery / Click & Collect)
    const fulfilmentDeliveryTab = document.getElementById('fulfilmentDeliveryTab');
    const fulfilmentCollectTab = document.getElementById('fulfilmentCollectTab');
    if (fulfilmentDeliveryTab) {
        fulfilmentDeliveryTab.addEventListener('click', function() {
            popupSetFulfilmentMethod('delivery');
        });
    }
    if (fulfilmentCollectTab) {
        fulfilmentCollectTab.addEventListener('click', function() {
            popupSetFulfilmentMethod('collect');
        });
    }

    // Studio selection — behaves exactly like picking the studio's suburb in
    // the autocomplete, with the postcode fixed; the date chosen next is the
    // collection date.
    document.querySelectorAll('.popup-studio-option').forEach(function(studioBtn) {
        studioBtn.addEventListener('click', function() {
            popupFulfilmentMethod = 'collect';
            popupSelectedStudio = this.getAttribute('data-studio');
            popupSetupDatePicker(
                this.getAttribute('data-locality'),
                this.getAttribute('data-state'),
                this.getAttribute('data-pcode')
            );
            fbPickerTrack('suburb', { method: 'collect' });
        });
    });

    // Input field event listener
    if (inputField) {
        // Kick off the (deferred) suburb-data fetch as soon as the user reaches the field
        inputField.addEventListener('focus', ensureSuburbData);
        inputField.addEventListener('input', function() {
            ensureSuburbData();
            // Reset to location step when user starts typing again
            const currentStep = document.querySelector('.popup-step.active').id;
            if (currentStep !== 'locationStep') {
                resetToLocationStep();
            }
            showResults(this.value.trim());
        });

        // Allow user to click on input field to search again WITHOUT transition
        inputField.addEventListener('click', function() {
            ensureSuburbData();
            const currentStep = document.querySelector('.popup-step.active').id;
            if (currentStep !== 'locationStep') {
                resetToLocationStep();
            }
            // Clear the input field when clicked
            this.value = '';
            this.focus();
            showResults(this.value.trim());
        });
    }

    // Click outside to close autocomplete
    document.addEventListener('click', function(event) {
        if (inputField && resultsContainer && 
            !inputField.contains(event.target) && 
            !resultsContainer.contains(event.target)) {
            resultsContainer.style.display = 'none';
        }
    });

   // Add close button event listener
    const popupCloseBtn = document.getElementById('popupCloseBtn');
    if (popupCloseBtn) {
        popupCloseBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            // User chose not to complete an interrupted add-to-cart — drop it
            window.pendingDeliveryAdd = null;
            try {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({ event: 'delivery_popup_dismiss' });
            } catch (err) { /* analytics must never break the popup */ }
            closePopup();
        });
    }
    
    // Pickup time step (Click & Collect): Back and the date's Edit link both return to the date step
    ['pickupBackBtn', 'pickupEditDateBtn'].forEach(function(id) {
        const btn = document.getElementById(id);
        if (btn) {
            btn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                showDateFromPickupTimeStep();
            });
        }
    });
}

// Optional: Add a function to manually trigger popup check after page load
function manualPopupCheck() {
    if (document.readyState === 'complete') {
        checkAndShowPopup();
    } else {
        window.addEventListener('load', checkAndShowPopup);
    }
}

function popupGetPreSaleProductUnavailabilityDates(location, productId) {
    if (!productId) return [];
    
    // First check window.productsWithDateRestrictions
    if (window.productsWithDateRestrictions && window.productsWithDateRestrictions[productId]) {
        const product = window.productsWithDateRestrictions[productId];
        if (product.dateRestrictions) {
            let locationKey = location + '_unavailable_dates';
            if (product.dateRestrictions[locationKey] && Array.isArray(product.dateRestrictions[locationKey])) {
                return product.dateRestrictions[locationKey];
            }
        }
    }
    
    // Fallback: read from HTML data attributes
    const productElement = document.querySelector(`[data-product-id="${productId}"]`);
    if (productElement) {
        // P5: was a 4-city switch whose `default` returned [] — so the popup calendar
        // never blocked a 5th location's unavailable dates. Legacy slugs keep the
        // original four byte-identical (RestofAustralia's is 'rest-australia', NOT
        // lowercase(keyPrefix)); any other configured location uses its lowercased
        // keyPrefix, matching snippets/fb-location-date-attrs.liquid. Still returns []
        // for a genuinely unknown location, exactly as before.
        const FB_LEGACY_SLUG = { Brisbane: 'brisbane', Melbourne: 'melbourne', Sydney: 'sydney', RestofAustralia: 'rest-australia' };
        let fbSlug = FB_LEGACY_SLUG[location];
        if (!fbSlug) {
            const fbKnown = (typeof window.fbLocationId === 'function') && !!window.fbLocationId(location);
            if (!fbKnown) return [];
            fbSlug = String(location).toLowerCase();
        }
        const locationKey = 'data-unavailable-' + fbSlug;
        
        const unavailableDatesStr = productElement.getAttribute(locationKey);
        if (unavailableDatesStr) {
            try {
                // Parse the JSON string that's HTML encoded
                const decoded = unavailableDatesStr.replace(/&quot;/g, '"');
                return JSON.parse(decoded);
            } catch (e) {
                console.warn('Error parsing unavailable dates:', e);
                return [];
            }
        }
    }
    
    return [];
}

// Update the popupGetCombinedBlockedDatesPreSale function - this is missing
function popupGetCombinedBlockedDatesPreSale(datePickerSettings, location, productId) {
    var appBlockedDates = datePickerSettings.selectedBlockedDates || [];
    var productUnavailableDates = [];
    
    // For pre-sale, get unavailable dates for the specific product only
    if (productId && window.pendingPreSaleForm) {
        productUnavailableDates = popupGetPreSaleProductUnavailabilityDates(location, productId);
    } else {
        // For regular flow, get all product unavailable dates
        productUnavailableDates = popupGetProductUnavailabilityDates(location);
    }
    
    var combinedDates = [...new Set([...appBlockedDates, ...productUnavailableDates])];
    return combinedDates;
}




// Update openPreSalePopup function to accept productId
function openPreSalePopup(productId = null) {
    // Check if location cookies exist
    var locality = getCookie('deliverySuburb');
    var state = getCookie('deliveryState');
    var pcode = getCookie('deliveryPostcode');
    
    if (!locality || !pcode || !state) {
        alert('Please select your delivery location first before adding pre-sale items.');
        return;
    }
    
    // Store the product ID for pre-sale logic
    window.currentPreSaleProductId = productId;
    
    // Open the popup
    if (typeof openPopup === 'function') {
        openPopup();
        
        setTimeout(function() {
            // Setup date picker for pre-sale with product ID
            popupSetupDatePickerForPreSale(locality, state, pcode, productId);
        }, 100);
    }
}

function popupSetupDatePickerForPreSale(locality, state, pcode, productId = null) {
    
    popupSelectedLocality = locality;
    popupSelectedState = state;
    popupSelectedPcode = pcode;
    
    // Store the product ID for pre-sale specific logic
    window.currentPreSaleProductId = productId;
    
    // Hide autocomplete results
    const resultsContainer = document.getElementById('popupAutocompleteResults');
    if (resultsContainer) resultsContainer.style.display = 'none';
    
    // Update the selected location display
    const selectedLocationText = document.getElementById('selectedLocationText');
    if (selectedLocationText) {
        selectedLocationText.textContent = `${locality}, ${pcode}`;
    }
    
     // Hide edit button for pre-sale
    const editLocationBtn = document.getElementById('editLocationBtn');
    if (editLocationBtn) {
        editLocationBtn.style.display = 'none';
    }
    // Skip location step and go directly to date selection
    showStep('dateStep');

    // Find location and get date picker settings
    popupCurrentLocation = findPostcodeLocation(pcode);

    // Courier expectations notice — same rule as the regular flow (StarTrack
    // regions are everything outside the three studio cities). Also clears a
    // notice left visible by an earlier regular setup for another region.
    var fbPreSaleCourier = ['Sydney', 'Melbourne', 'Brisbane'].indexOf(popupCurrentLocation) === -1;
    var fbPreSaleCourierNotice = document.getElementById('popupCourierNotice');
    if (fbPreSaleCourierNotice) {
        fbPreSaleCourierNotice.style.display = fbPreSaleCourier ? 'flex' : 'none';
    }
    var fbPreSaleCourierNoticeCal = document.getElementById('popupCourierNoticeCalendar');
    if (fbPreSaleCourierNoticeCal) {
        fbPreSaleCourierNoticeCal.style.display = fbPreSaleCourier ? 'flex' : 'none';
    }

    // Pre-sale is always a delivery flow, so pin the date button label back in
    // case a prior collect ("Select Collection Date") setup left a different
    // noun on it. StarTrack regions keep the regular flow's "Preferred" wording,
    // because the chosen date is a target there too.
    const preSaleDateButton = document.getElementById('popupSelectDate');
    if (preSaleDateButton) {
        preSaleDateButton.textContent = fbPreSaleCourier ? 'Select Preferred Delivery Date' : 'Select Delivery Date';
    }

    // Get date picker settings with fallback
    // P5: one resolver for ALL locations — prefers the app's per-location config and
    // falls back to the legacy four internally. Replaces a 4-city switch whose `default`
    // set null, which is why a 5th location (Adelaide) silently dropped to the generic
    // defaults below instead of using its own timezone / cutoff / working days. The popup
    // already depended on the extension for the {City}DatePickerArray globals, so this
    // adds no new dependency.
    popupDatePickerSettings = (typeof window.fbGetLocationDateSettings === 'function')
        ? window.fbGetLocationDateSettings(popupCurrentLocation)
        : null;

    // Deploy-order safety net: the resolver above lives in the app extension. Until that
    // extension is deployed it is undefined, and WITHOUT this the pre-sale picker would drop
    // every legacy city to the generic defaults below. Read the per-city globals exactly as
    // the pre-P5 switch did, so the theme is safe to ship in either order.
    if (!popupDatePickerSettings) {
        var fbLegacyPreSale = window[popupCurrentLocation + 'DatePickerArray'];
        if (fbLegacyPreSale && fbLegacyPreSale.DatePickerSettings) {
            popupDatePickerSettings = fbLegacyPreSale.DatePickerSettings[0] || null;
        }
    }


    // Create default settings if none found
    if (!popupDatePickerSettings) {
        console.warn('Date picker settings not found for location:', popupCurrentLocation, 'using defaults');
        popupDatePickerSettings = {
            timezone: 'Australia/Sydney',
            cutoffTime: '13:00',
            minDeliveryDays: 1,
            maxDeliveryDays: 14,
            workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            selectedBlockedDates: [],
            specialDays: [],
            firstDay: 'Monday',
            activeCutoff: true
        };
    }
    
    // Show pre-sale message
    showPreSaleMessage();
    
    // Hide cutoff time display for pre-sale
    const cutoffDisplay = document.getElementById('popupCutoffTimeDisplay');
    if (cutoffDisplay) {
        cutoffDisplay.style.display = 'none';
    }
    
    // Setup date calculations
    const dayMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
    const nowInAustraliaTimezone = popupGetAustraliaDate(popupDatePickerSettings.timezone);
    const now = nowInAustraliaTimezone.toDate();
    const timezone = popupDatePickerSettings.timezone;
    
    // Pre-sale uses the shared minimum delivery days.
const baseMinDeliveryDays = Math.max(1, getMinDeliveryDays(popupDatePickerSettings));
let minDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + baseMinDeliveryDays);
const centrepieceLead = window.fbCentrepieceLeadTime;
const centrepieceEarliest = centrepieceLead && centrepieceLead.applies(productId) ? centrepieceLead.earliestForLocation(popupCurrentLocation) : null;
if (centrepieceEarliest) {
    const parts = centrepieceEarliest.split('-').map(Number);
    const leadMin = new Date(parts[0], parts[1] - 1, parts[2]);
    if (leadMin > minDate) minDate = leadMin;
}
const maxDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + parseInt(popupDatePickerSettings.maxDeliveryDays));

    
    // FIXED: Get combined blocked dates including product-specific unavailable dates for pre-sale
    const combinedBlockedDates = popupGetCombinedBlockedDatesPreSale(popupDatePickerSettings, popupCurrentLocation, productId);
    
    // Check if today is available - UPDATED to check product-specific unavailable dates
    const isTodayWorkingDay = popupDatePickerSettings.workingDays.map(day => dayMap[day]).includes(nowInAustraliaTimezone.day());
    // Get product-specific unavailable dates for today check
    const productUnavailableDates = popupGetPreSaleProductUnavailabilityDates(popupCurrentLocation, productId);
    const isTodayBlocked = productUnavailableDates.some(blockedDate => {
        const blockedMoment = popupCreateAustraliaDate(blockedDate, timezone);
        return blockedMoment.format('YYYY-MM-DD') === nowInAustraliaTimezone.format('YYYY-MM-DD');
    });

    // Also check app-level blocked dates
    const appBlockedDates = popupDatePickerSettings.selectedBlockedDates || [];
    const isTodayAppBlocked = appBlockedDates.some(blockedDate => {
        const blockedMoment = popupCreateAustraliaDate(blockedDate, timezone);
        return blockedMoment.format('YYYY-MM-DD') === nowInAustraliaTimezone.format('YYYY-MM-DD');
    });

    // ADDED: Check capacity for today in pre-sale
    const isTodayCapacityReached = popupIsCapacityReachedForToday(popupCurrentLocation);

  // Setup Today button visibility for pre-sale
const todayButton = document.getElementById('popupSelectToday');
const canDeliverTodayPreSale = baseMinDeliveryDays === 0 && !centrepieceEarliest;

// Check recurring unavailable days for pre-sale Today button
const isTodayRecurringUnavailablePreSale = popupDatePickerSettings.recurringUnavailableDays &&
    popupDatePickerSettings.recurringUnavailableDays.map(function(day) { return dayMap[day]; })
    .includes(nowInAustraliaTimezone.day());

if (!isTodayWorkingDay || isTodayBlocked || isTodayAppBlocked || isTodayCapacityReached || isTodayRecurringUnavailablePreSale || popupCurrentLocation === "RestofAustralia" || !canDeliverTodayPreSale) {
    todayButton.style.display = 'none';
    console.log('Pre-sale Today button hidden. Min days:', baseMinDeliveryDays);
} else {
    todayButton.style.display = 'inline-block';
    todayButton.textContent = 'Today';
}
    
    // Initialize Flatpickr
    if (popupFlatpickr) {
        popupFlatpickr.destroy();
    }
    // flatpickr.destroy() does not remove the temp input we created for it
    if (window.__popupFlatpickrTempInput && window.__popupFlatpickrTempInput.parentNode) {
        window.__popupFlatpickrTempInput.remove();
    }

    const tempInput = document.createElement('input');
    tempInput.type = 'text';
    tempInput.style.display = 'none';
    document.body.appendChild(tempInput);
    window.__popupFlatpickrTempInput = tempInput;

    popupFlatpickr = flatpickr(tempInput, {
        enableTime: false,
        dateFormat: "l, J F Y",
        minDate: minDate,
        maxDate: maxDate,
        inline: true,
        static: false,
        disable: [
            function(date) {
                // FIXED: Get combined blocked dates including product-specific unavailable dates for pre-sale
                const combinedBlockedDates = popupGetCombinedBlockedDatesPreSale(popupDatePickerSettings, popupCurrentLocation, productId);
                
                const localYear = date.getFullYear();
                const localMonth = date.getMonth();
                const localDay = date.getDate();
                const dateMoment = moment.tz([localYear, localMonth, localDay], timezone);
                const dateFormatted = dateMoment.format('YYYY-MM-DD');
                
                const isBlocked = combinedBlockedDates.some(blockedDate => {
                    const blockedMoment = popupCreateAustraliaDate(blockedDate, timezone);
                    return blockedMoment.format('YYYY-MM-DD') === dateFormatted;
                });

                const isSpecialDay = popupDatePickerSettings.specialDays && popupDatePickerSettings.specialDays.some(specialDate => {
                    const specialMoment = popupCreateAustraliaDate(specialDate, timezone);
                    return specialMoment.format('YYYY-MM-DD') === dateFormatted;
                });

                const isWorkingDay = popupDatePickerSettings.workingDays.map(day => dayMap[day]).includes(date.getDay());
                
                // ADDED: Check delivery capacity for this location and date in pre-sale
                const isCapacityReached = popupCheckDeliveryCapacity(popupCurrentLocation, dateFormatted);
                if (isCapacityReached) {
                   
                    return true;
                }

                return !isSpecialDay && (isBlocked || !isWorkingDay);
            }
        ],
onChange: async function(selectedDates, dateStr, instance) {
    if (selectedDates.length > 0) {
        const selectedMoment = moment(dateStr, "dddd, Do MMMM YYYY");
        const selectedISODate = moment.tz(selectedMoment.format('YYYY-MM-DD'), timezone).startOf('day').format();
        const selectedDate = selectedMoment.format('YYYY-MM-DD');
        
        // Clear any previous messages
        hidePreSaleCapacityMessage();
        
        // Show loading overlay on calendar
        showPreSaleCalendarLoading();
        
        // Check capacity
        const isCapacityReached = await checkPreSaleCapacityWithAPI(popupCurrentLocation, selectedDate);
        
        // Hide loading overlay
        hidePreSaleCalendarLoading();
        
        if (isCapacityReached) {
            // Show error message above calendar
            showPreSaleCapacityMessage('This date has reached delivery capacity. Please select another date.', 'error');
            
            // Clear the selection
            instance.clear();
            return;
        }
        
        // Success - proceed with selection
        popupFinishSelectionPreSale(dateStr, selectedISODate);
    }
},
        locale: {
            firstDayOfWeek: dayMap[popupDatePickerSettings.firstDay] || 1,
        }
    });

    // Same as popupSetupDatePicker: keep the body-mounted inline calendar hidden
    // until showCalendarStep moves it into the popup (sets display:block).
    if (popupFlatpickr.calendarContainer) {
        popupFlatpickr.calendarContainer.style.display = 'none';
    }
}


function showPreSaleCapacityMessage(message, type = 'error') {
    const messageElement = document.getElementById('presaleCapacityMessage');
    if (messageElement) {
        messageElement.textContent = message;
        messageElement.className = `presale-capacity-message ${type}`;
        messageElement.style.display = 'flex';
    }
}

function hidePreSaleCapacityMessage() {
    const messageElement = document.getElementById('presaleCapacityMessage');
    if (messageElement) {
        messageElement.style.display = 'none';
    }
}

function showPreSaleCalendarLoading() {
    const overlay = document.getElementById('calendarLoadingOverlay');
    if (overlay) {
        overlay.classList.add('active');
    }
    // Also make calendar container position relative for overlay
    const container = document.getElementById('calendarContainer');
    if (container) {
        container.style.position = 'relative';
    }
}

function hidePreSaleCalendarLoading() {
    const overlay = document.getElementById('calendarLoadingOverlay');
    if (overlay) {
        overlay.classList.remove('active');
    }
}




function showPreSaleMessage() {
    // Update the date header to show pre-sale message
    const dateHeader = document.querySelector('.popup-date-header p');
    if (dateHeader) {
        dateHeader.innerHTML = '<strong style="font-size:16px;">This Product is available for Pre-Order</strong><br><span style="font-size: 14px; color: #666; font-weight: normal;">Select your delivery date and be among the first to receive it.</span>';
    }
    
    // Hide the cutoff time display for pre-sale
    const cutoffDisplay = document.getElementById('popupCutoffTimeDisplay');
    if (cutoffDisplay) {
        cutoffDisplay.style.display = 'none';
    }
    
    // Update Today button text if visible
    const todayButton = document.getElementById('popupSelectToday');
    if (todayButton && todayButton.style.display !== 'none') {
        const originalText = todayButton.textContent;
        todayButton.textContent = 'Today (Pre-Sale)';
        todayButton.setAttribute('data-original-text', originalText);
    }
}

function popupPreSaleDateIsAvailable(isoDate, productId) {
    const day = typeof isoDate === 'string' ? isoDate.split('T')[0] : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
    const parsed = new Date(day + 'T00:00:00Z');
    if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== day) return false;
    const today = popupGetAustraliaDate(popupDatePickerSettings.timezone).format('YYYY-MM-DD');
    if (day <= today) return false;
    const blocked = popupGetCombinedBlockedDatesPreSale(popupDatePickerSettings, popupCurrentLocation, productId);
    return !blocked.some(date => String(date).split('T')[0] === day);
}

async function popupFinishSelectionPreSale(dateStr, isoDate) {
    if (!popupPreSaleDateIsAvailable(isoDate, window.currentPreSaleProductId)) {
        if (typeof window.popupShowUnavailableToast === 'function') {
            window.popupShowUnavailableToast(null, 'Please choose an available future delivery date for this pre-order.');
        }
        return;
    }

    const centrepiece = window.fbCentrepieceLeadTime;
    const pending = window.pendingDeliveryAdd;
    const leadProductId = pending && pending.getProductId ? pending.getProductId() : (window.currentPreSaleProductId || null);
    if (centrepiece && centrepiece.invalidDate(isoDate, popupSelectedPcode, leadProductId)) {
        if (typeof window.popupShowUnavailableToast === 'function') window.popupShowUnavailableToast(null, 'Please choose an available delivery date.');
        return;
    }

    // Show loading state in date header
    const dateHeader = document.querySelector('.popup-date-header p');
    const originalContent = dateHeader ? dateHeader.innerHTML : '';
    
    if (dateHeader) {
        dateHeader.innerHTML = '<strong>Checking availability...</strong> <span style="display: inline-block; margin-left: 10px;">⏳</span>';
    }
    
    const selectedDate = isoDate.split('T')[0];
    
    try {
        // Check capacity
        const isCapacityReached = await checkPreSaleCapacityWithAPI(popupCurrentLocation, selectedDate);
        
        if (isCapacityReached) {
            if (dateHeader) {
                dateHeader.innerHTML = '<strong style="color: #222222; font-size:13px;">This date has reached capacity</strong><br><span style="font-size: 13px; color: #666;">Please select another delivery date.</span>';
            }
            return;
        }
        
        // Capacity available, proceed
        fbPickerTrack('date', { flow: 'presale' });
        if (window.pendingPreSaleForm) {
            submitPreSaleFormWithDate(window.pendingPreSaleForm, dateStr, isoDate);
        }
        
        window.pendingPreSaleForm = null;
        window.currentPreSaleProductId = null;
        closePopup();
        
    } catch (error) {
        console.error('Error checking capacity:', error);
        
        // Restore original content on error
        if (dateHeader) {
            dateHeader.innerHTML = originalContent;
        }
        
        // On error, allow submission (fail open)
        if (window.pendingPreSaleForm) {
            submitPreSaleFormWithDate(window.pendingPreSaleForm, dateStr, isoDate);
        }
        window.pendingPreSaleForm = null;
        window.currentPreSaleProductId = null;
        closePopup();
    }
}


async function checkPreSaleCapacityWithAPI(location, dateString) {
    // First try API
    if (window.deliveryAppConfig && window.deliveryAppConfig.apiBaseUrl) {
        try {
            const apiUrl = `${window.deliveryAppConfig.apiBaseUrl}/live-capacity?location=${encodeURIComponent(location)}`;
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data && data.data[dateString]) {
                    const capacity = data.data[dateString];
                    return !capacity.available;
                }
            }
        } catch (error) {
            console.log('API check failed, using fallback:', error);
        }
    }
    
    // Fallback to metafield check
    return popupCheckDeliveryCapacity(location, dateString);
}


function submitPreSaleFormWithDate(form, dateStr, isoDate) {
    // Get existing delivery data from cookies (don't update them)
    const deliverySuburb = getCookie('deliverySuburb') || '';
    const deliveryPostcode = getCookie('deliveryPostcode') || '';
    
   
    
    // Get or create ALL delivery inputs in the correct order
    let suburbInput = form.querySelector('input[name="properties[Suburb]"]');
    let postcodeInput = form.querySelector('input[name="properties[Postcode]"]');
    let dateInput = form.querySelector('input[name="properties[Delivery Date]"]');
    let dateISOInput = form.querySelector('input[name="properties[_ISODate]"]');
    
    // Create suburb input if it doesn't exist
    if (!suburbInput) {
        suburbInput = document.createElement('input');
        suburbInput.type = 'hidden';
        suburbInput.name = 'properties[Suburb]';
        suburbInput.classList.add('delivery-suburb-property');
        form.appendChild(suburbInput);
    }
    
    // Create postcode input if it doesn't exist
    if (!postcodeInput) {
        postcodeInput = document.createElement('input');
        postcodeInput.type = 'hidden';
        postcodeInput.name = 'properties[Postcode]';
        postcodeInput.classList.add('delivery-postcode-property');
        form.appendChild(postcodeInput);
    }
    
    // Create date inputs if they don't exist (AFTER suburb and postcode)
    if (!dateInput) {
        dateInput = document.createElement('input');
        dateInput.type = 'hidden';
        dateInput.name = 'properties[Delivery Date]';
        dateInput.classList.add('delivery-date-property');
        form.appendChild(dateInput);
    }
    if (!dateISOInput) {
        dateISOInput = document.createElement('input');
        dateISOInput.type = 'hidden';
        dateISOInput.name = 'properties[_ISODate]';
        dateISOInput.classList.add('delivery-date-iso-property');
        form.appendChild(dateISOInput);
    }
    
    // Set all values
    suburbInput.value = deliverySuburb;
    postcodeInput.value = deliveryPostcode;
    dateInput.value = dateStr;
    dateISOInput.value = isoDate;
    
    // Check if this is a product-form web component
    const productFormComponent = form.closest('product-form');
    
    if (productFormComponent) {
        // If it's a web component, call submitForm method
        console.log('Submitting via product-form web component');
        productFormComponent.submitForm();
    } else {
        // For regular forms (fallback)
        console.log('Submitting via regular form');
        form.submit();
    }
}


function submitPreSaleForm(form, dateStr, isoDate) {
    // Get delivery data from cookies
    const deliverySuburb = getCookie('deliverySuburb') || '';
    const deliveryPostcode = getCookie('deliveryPostcode') || '';
    

    
    // Check if this is a product-form web component
    const productFormComponent = form.closest('product-form');
    
    if (productFormComponent) {
        // If it's a web component, call its submitForm method
        console.log('Submitting via product-form web component');
        productFormComponent.submitForm();
    } else {
        // For regular forms (like the ones in product-card-item)
        console.log('Submitting via regular form');
        
        // Update the hidden inputs in the form with the new date
        const suburbInput = form.querySelector('.delivery-suburb-property') || 
                           form.querySelector('input[name="properties[Suburb]"]');
        const postcodeInput = form.querySelector('.delivery-postcode-property') || 
                             form.querySelector('input[name="properties[Postcode]"]');
        const dateInput = form.querySelector('.delivery-date-property') || 
                         form.querySelector('input[name="properties[Delivery Date]"]');
        const dateISOInput = form.querySelector('.delivery-date-iso-property') || 
                            form.querySelector('input[name="properties[_ISODate]"]');
        
        if (suburbInput) suburbInput.value = deliverySuburb;
        if (postcodeInput) postcodeInput.value = deliveryPostcode;
        if (dateInput) dateInput.value = dateStr;
        if (dateISOInput) dateISOInput.value = isoDate;
        
        // Submit the form
        form.submit();
    }
}
